import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Slide from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
    Banknote,
    Barcode,
    CheckCircle2,
    ChevronDown,
    CircleUserRound,
    CreditCard,
    DollarSign,
    LogOut,
    Minus,
    Moon,
    Package,
    Pencil,
    Plus,
    Printer,
    QrCode,
    Receipt,
    ShoppingCart,
    Sun,
    Tag,
    Trash2,
    UserPlus,
    Wallet,
    X,
    XCircle
} from 'lucide-react';
import { standaloneFieldSx, standaloneFloatingSelectSx } from '../common/fieldStyles';
import { formatCurrency } from '../common/formatters';
import { httpClient } from '../common/httpclient';
import DefaultAutocomplete from '../component/autocomplete/DefaultAutocomplete';
import DialogForm from '../component/dialog/DialogForm';
import { useEnterpriseConfigContext } from '../component/context/EnterpriseConfigContext';
import QuickCustomerDrawer from '../component/QuickCustomerDrawer';
import MobileSaleView, { MobileSaleSkeleton } from '../component/sale/MobileSaleView';
import { useColorMode } from '../hooks/useColorMode';
import { useIsMobileShell } from '../hooks/useIsMobileShell';
import { useCompanyLogo } from '../hooks/useCompanyLogo';
import { usePermissions } from '../hooks/usePermissions';
import ProductManagerService from '../services/ProductManagerService';
import { POS_GRADIENT } from '../theme/tokens';

/**
 * Nova Venda (POS) — `/sale`.
 *
 * Tela full-screen dedicada (DESIGN-SYSTEM.md §2.4 / §3.4 / §6.3): sem
 * sidebar/topbar do shell padrão (a rota já vive fora do `MainView` em
 * `DefaultRouter.js`), app bar em gradiente escopado da POS (nunca o
 * gradiente de marca do shell normal) e um corpo 66.67% / 33.33% (busca +
 * carrinho em lista à esquerda, caixa/ações/totais à direita). O drawer de
 * pagamento segue o padrão de acordeão de métodos do §5.7.
 *
 * Este componente NÃO cria seu próprio `ThemeProvider`/`createTheme` — ele
 * herda o tema global (`App.js` → `buildAppTheme(mode)` via `useColorMode`),
 * que já traz os overrides de `Button`/`TextField`/`Dialog`/`Drawer` etc. da
 * Fundação. Um `ThemeProvider` local aqui pisaria nesses overrides (é a
 * classe de bug documentada em `feedback_input_label_theme_bug.md`).
 */

const posGradient = (mode) => `linear-gradient(135deg, ${POS_GRADIENT.pos1[mode]} 0%, ${POS_GRADIENT.pos2[mode]} 100%)`;

const PosShell = styled(Box)(({ theme }) => ({
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
    overflow: 'hidden',
    // dvh: a barra de endereço do navegador mobile não corta o rodapé da tela
    '@supports (height: 100dvh)': { height: '100dvh' }
}));

const PosAppBar = styled(Box)(({ theme }) => ({
    height: 70,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 28px',
    color: '#fff',
    background: posGradient(theme.palette.mode),
    borderBottom: '1px solid rgba(255,255,255,.12)',
    [theme.breakpoints.down('sm')]: { height: 60, padding: '0 14px', gap: 8 }
}));

const GhostIconButton = styled(IconButton)(() => ({
    width: 36,
    height: 36,
    borderRadius: 9,
    border: '1px solid rgba(255,255,255,.28)',
    backgroundColor: 'rgba(255,255,255,.08)',
    color: '#fff',
    '&:hover': { backgroundColor: 'rgba(255,255,255,.18)' },
    '@media (pointer: coarse)': { width: 38, height: 38 }
}));

const GhostIconButtonDanger = styled(GhostIconButton)(() => ({
    color: '#fecaca',
    backgroundColor: 'rgba(239,68,68,.18)',
    '&:hover': { backgroundColor: 'rgba(239,68,68,.32)' }
}));

// Abaixo de `md` (<900px: celular e tablet retrato) o corpo empilha: busca +
// carrinho em cima (rola), painel de caixa/totais/finalizar embaixo, sempre
// visível. Em >= md (desktop e tablet paisagem) segue o 66/33 original.
const PosBody = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    minHeight: 0,
    [theme.breakpoints.down('md')]: { flexDirection: 'column' }
}));

const PosLeft = styled(Box)(({ theme }) => ({
    flex: '0 0 66.6667%',
    padding: 22,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
    [theme.breakpoints.down('md')]: { flex: '1 1 0', padding: 14 }
}));

const PosRight = styled(Box)(({ theme }) => ({
    flex: '0 0 33.3333%',
    background: posGradient(theme.palette.mode),
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    padding: 22,
    overflowY: 'auto',
    minWidth: 0,
    [theme.breakpoints.down('md')]: {
        flex: '0 0 auto',
        maxHeight: '48%',
        padding: '12px 14px calc(12px + env(safe-area-inset-bottom))'
    }
}));

const SearchCard = styled(Box)(({ theme }) => ({
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    padding: 18,
    marginBottom: 16,
    position: 'relative',
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: { padding: 12, marginBottom: 10 }
}));

const SearchLabel = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '.07em',
    color: theme.palette.primary.main,
    marginBottom: 10,
    textTransform: 'uppercase'
}));

const CartCard = styled(Box)(({ theme }) => ({
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    padding: 8,
    flex: 1,
    overflowY: 'auto',
    minHeight: 340,
    [theme.breakpoints.down('md')]: { minHeight: 180 }
}));

const CartItemRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 10px',
    borderRadius: 12,
    margin: 4,
    '&:hover': { backgroundColor: theme.palette.background.subtle },
    // celular: miniatura + dados na 1ª linha; quantidade, total e ações quebram para a 2ª
    [theme.breakpoints.down('sm')]: { flexWrap: 'wrap', gap: 8, margin: '4px 0', padding: '10px 6px' }
}));

const ItemThumb = styled(Box)(({ theme }) => ({
    width: 46,
    height: 46,
    borderRadius: '50%',
    background: theme.palette.info.bg,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
    marginTop: 2
}));

const QtyButton = styled(IconButton)(({ theme, tone }) => ({
    width: 26,
    height: 26,
    borderRadius: 8,
    color: '#fff',
    backgroundColor: tone === 'minus' ? theme.palette.error.main : theme.palette.success.main,
    '&:hover': {
        backgroundColor: tone === 'minus' ? theme.palette.error.dark : theme.palette.success.dark
    },
    '&.Mui-disabled': { opacity: 0.5, color: '#fff' },
    '@media (pointer: coarse)': { width: 32, height: 32 },
    [theme.breakpoints.down('sm')]: { width: 26, height: 26 }
}));

const RowIconButton = styled(IconButton)(({ theme }) => ({
    width: 28,
    height: 28,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.disabled,
    '&:hover': { backgroundColor: theme.palette.background.subtle },
    '&.Mui-disabled': { opacity: 0.5 },
    '@media (pointer: coarse)': { width: 34, height: 34 },
    [theme.breakpoints.down('sm')]: { width: 28, height: 28 }
}));

const PosChip = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    background: 'rgba(255,255,255,.1)',
    padding: '9px 12px',
    borderRadius: 20,
    '@media (max-width: 899.95px)': { padding: '5px 10px', fontSize: 11.5 }
}));

const FinalizeButton = styled(Button)(({ theme }) => ({
    width: '100%',
    height: 50,
    borderRadius: 12,
    border: 'none',
    backgroundColor: '#fff',
    color: POS_GRADIENT.pos2[theme.palette.mode],
    fontWeight: 800,
    fontSize: 14,
    marginTop: 16,
    [theme.breakpoints.down('md')]: { height: 42, marginTop: 10 },
    '&:hover': { backgroundColor: '#fff', filter: 'brightness(.97)' },
    '&.Mui-disabled': { opacity: 0.5, color: POS_GRADIENT.pos2[theme.palette.mode] }
}));

const PayMethodCard = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    marginBottom: 10,
    overflow: 'hidden'
}));

const PayMethodHead = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    cursor: 'pointer'
}));

const PayMethodIcon = styled(Box)(({ theme }) => ({
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: theme.palette.info.bg,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
}));

const PaidRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    background: theme.palette.background.subtle,
    borderRadius: 10,
    marginBottom: 8,
    fontSize: 12.5
}));

// API Service Functions
const saleService = {
    createSale: async (customerId) => {
        return await httpClient.post('/sales', { customer_id: customerId || '' });
    },
    setSaleCustomer: async (saleId, customerId) => {
        return await httpClient.post(`/sales/${saleId}/customer`, { customer_id: customerId || '' });
    },
    getCustomers: async () => {
        return await httpClient.get('/customers');
    },

    applyDiscount: async (saleId, discount) => {
        return await httpClient.post(`/sales/discount/apply`, { sale_id: saleId, value: discount });
    },
    applyItemDiscount: async (payload) => {
        return await httpClient.post('/sales/item/discount', payload);
    },
    getSaleItems: async (saleId) => {
        return await httpClient.get(`/sales/${saleId}/items`);
    },
    getInstallments: async (saleId, saleTotal, maxInstallments) => {
        return await httpClient.post(`/sales/${saleId}/installments`, { sale_id: saleId, value: saleTotal, max_installments: maxInstallments });
    },
    getMachineCards: async () => {
        return await httpClient.get(`/cardmachines`);
    },
    getPaymentId: async (paymentName) => {
        const response = await httpClient.get(`/sales/paymentid?name=${paymentName}`);
        return response.data.id;
    },
    payment: async (saleId, payments) => {
        return await httpClient.post(`/sales/${saleId}/payment`, payments);
    },
    finishSale: async (saleId) => {
        return await httpClient.post(`/sales/${saleId}/finish`, {});
    },
    getChangeCash: async (amount, received) => {
        const response = await httpClient.get(`/sales/payment/cashchange?amount=${amount}&received=${received}`);
        return response;
    },
    getRemainingValue: async (saleId, payments) => {
        const response = await httpClient.post(`/sales/${saleId}/reamining`, payments);
        return response;
    },
    cancelSale: async (saleId, reason) => {

        return await httpClient.post(`/sales/cancel/${saleId}`, { reason: reason });
    },
    getSaleToContinue: async (saleId) => {
        return await httpClient.get(`/sales/${saleId}/continue`);
    },
    getCashRegisterList: async () => {
        return await httpClient.get(`/cashregister`);
    },
    printSale: async (saleId) => {
        return await httpClient.post(`/sales/print?saleId=${saleId}`);
    },
    selectCashRegister: async (saleId, cashRegisterId) => {
        return await httpClient.post(`/cashregister/${cashRegisterId}/select`, {});
    },
    getCashRegister: async (cashRegisterId) => {
        return await httpClient.get(`/cashregister/lock`);
    },
    getSaleTotal: async (saleId) => {
        return await httpClient.get(`/sales/${saleId}/total`);
    },
    getSaleTotalItems: async (saleId) => {
        return await httpClient.get(`/sales/${saleId}/countitems`);
    }

};

const stockService = {
    getAvailableProducts: async () => {
        return await httpClient.get('/stock/available?showOutOfStock=false');
    },

    getProduct: async (id) => {
        return await httpClient.get(`/stock/edit/${id}`);
    },

    getProductByCode: async (code) => {
        const response = await httpClient.get(`/stock/productId?code=${code}`);
        return response.data.id;
    },

    checkStock: async (productId) => {
        return await httpClient.get(`/stock/${productId}/isAvailableQuantity`);
    }
};

/** Normaliza item da API (continue ou /items) para o formato do carrinho na tela de venda */
function mapApiSaleItemToCartItem(item, productList) {
    const productId = item.product_id || item.id;
    const productName = item.product_name || item.name || '';
    const image = item.image ?? productList?.find((p) => p.id === productId)?.image;
    return {
        ...item,
        id: item.id ?? productId,
        product_id: productId,
        product_name: productName,
        name: productName,
        price: item.value ?? item.price ?? '',
        image,
        item_discount_type: item.item_discount_type ?? '',
        item_discount_amount: item.item_discount_amount ?? '',
        subtotal: item.subtotal ?? '',
        is_generic: Boolean(item.is_generic ?? item.isGeneric)
    };
}

/** Mensagem de erro do JSON da API (httpClient resolve 4xx/5xx em vez de rejeitar). */
function messageFromApiErrorBody(data) {
    if (!data || typeof data !== 'object') return '';
    if (typeof data.error === 'string' && data.error.trim()) return data.error;
    if (typeof data.message === 'string' && data.message.trim()) return data.message;
    if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
    if (Array.isArray(data.detail)) {
        const parts = data.detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean);
        if (parts.length) return parts.join(', ');
    }
    return '';
}

// Loading inicial — mesma estrutura 70px app bar + 66.67/33.33, tokens da POS (§2.4)
const SaleSkeleton = () => (
    <PosShell>
        <PosAppBar>
            <Box display="flex" alignItems="center" gap={2}>
                <Skeleton variant="rectangular" width={44} height={44} sx={{ borderRadius: '11px', bgcolor: 'rgba(255,255,255,.2)' }} />
                <Box>
                    <Skeleton variant="text" width={150} height={28} sx={{ bgcolor: 'rgba(255,255,255,.2)' }} />
                    <Skeleton variant="text" width={120} height={16} sx={{ bgcolor: 'rgba(255,255,255,.2)' }} />
                </Box>
            </Box>
            <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: 'rgba(255,255,255,.2)' }} />
        </PosAppBar>
        <PosBody>
            <PosLeft>
                <SearchCard>
                    <Skeleton variant="text" width={110} height={16} sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" width="100%" height={52} sx={{ borderRadius: '12px' }} />
                </SearchCard>
                <CartCard>
                    {Array.from({ length: 4 }).map((_, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 1.5 }}>
                            <Skeleton variant="circular" width={46} height={46} />
                            <Box sx={{ flex: 1 }}>
                                <Skeleton variant="text" width="60%" height={20} />
                                <Skeleton variant="text" width="30%" height={16} />
                            </Box>
                            <Skeleton variant="text" width={60} height={24} />
                        </Box>
                    ))}
                </CartCard>
            </PosLeft>
            <PosRight>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {Array.from({ length: 4 }).map((_, index) => (
                        <Skeleton key={index} variant="rectangular" width={36} height={36} sx={{ borderRadius: '9px', bgcolor: 'rgba(255,255,255,.15)' }} />
                    ))}
                </Box>
                <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: '12px', bgcolor: 'rgba(255,255,255,.12)', mb: 2 }} />
                <Box sx={{ mt: 'auto' }}>
                    <Skeleton variant="rectangular" width="100%" height={90} sx={{ borderRadius: '12px', bgcolor: 'rgba(255,255,255,.12)', mb: 2 }} />
                    <Skeleton variant="rectangular" width="100%" height={50} sx={{ borderRadius: '12px', bgcolor: 'rgba(255,255,255,.85)' }} />
                </Box>
            </PosRight>
        </PosBody>
    </PosShell>
);

function SaleContent() {
    // Refs
    const tableRef = useRef(null);
    const theme = useTheme();
    const { companyLogo, isLoading: logoLoading } = useCompanyLogo();
    const { hasPermission } = usePermissions();
    const { mode: colorMode, toggleMode: toggleColorMode } = useColorMode();
    // Shell mobile (<768px): renderiza a variante mobile do PDV (espelha o fv-app).
    const isMobile = useIsMobileShell();
    // Configuração "Identificar cliente na venda" (aba Vendas); padrão ativo quando ausente.
    const { settings: enterpriseSettings } = useEnterpriseConfigContext();
    const identifyCustomerEnabled = enterpriseSettings?.sale_identify_customer !== false;
    const printReceiptEnabled = enterpriseSettings?.sale_print_receipt !== false;

    // Navigation
    const navigate = useNavigate();

    // State Management
    const [saleState, setSaleState] = useState({
        id: '',
        total: '0,00',
        totalItems: 0,
        cart: [],
        productList: [],
        total_discount: '0,00',
        total_item_discount: '0,00',
        subtotal: '0,00',
        remaining_value: '0,00',
        discount_applied: '0,00',
        reason: '',
        number: 0,
        cash_register_name: '',
        cash_register_id: '',
        hasError: false,
        errorMessage: ''
    });

    const [uiState, setUiState] = useState({
        openPayment: false,
        showInstallments: false,
        showSearch: false,
        showAttention: false,
        showSuccess: false,
        attentionMessage: '',
        searchProduct: '',
        searchProductByCode: '',
        installments: 1,
        isProductGeneric: false,
        fieldSearchFocus: true,
        showSelectCashRegister: false,
        cash_register_list: [],
        showPaymentOptions: false,
        showCreditCard: false,
        showDebitCard: false,
        showPix: false,
        showCash: false,
        useSearchByCode: false
    });

    const [discountState, setDiscountState] = useState({
        applied: false,
        value: '0,00',
        percentage: 0
    });
    const [companyData, setCompanyData] = useState({});
    // Cliente identificado na venda (opcional): { id, name } ou null.
    const [saleCustomer, setSaleCustomer] = useState(null);
    const [showCustomerSelect, setShowCustomerSelect] = useState(false);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [openDialogNewCustomer, setOpenDialogNewCustomer] = useState(false);
    const [paymentType, setPaymentType] = useState('single');
    const [creditValue, setCreditValue] = useState('');
    const [debitValue, setDebitValue] = useState(paymentType === 'single' ? saleState.total : '');
    const [pixValue, setPixValue] = useState(paymentType === 'single' ? saleState.total : '');
    const [cashValue, setCashValue] = useState(paymentType === 'single' ? saleState.total : '');
    const [selectedMachine, setSelectedMachine] = useState('stone');
    const [payments, setPayments] = useState([]);
    const [installments, setInstallments] = useState([]);
    const [machineCards, setMachineCards] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDialogSuccess, setOpenDialogSuccess] = useState(false);
    const [pixType, setPixType] = useState('key');
    const [disabledPayment, setDisabledPayment] = useState(false);
    const [openDialogCancelSale, setOpenDialogCancelSale] = useState(false);
    const [openDialogPrint, setOpenDialogPrint] = useState(false);
    const [openDialogGenericProduct, setOpenDialogGenericProduct] = useState(false);
    const [genericProductData, setGenericProductData] = useState({
        product_id: '',
        product_name: '',
        price: '',
        quantity: 1,
        item_discount_value: ''
    });
    const [itemDiscountDialog, setItemDiscountDialog] = useState({
        open: false,
        item: null,
        value: '',
        loading: false
    });
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');
    const [lastSaleId, setLastSaleId] = useState('');
    const [cashRegisterFeedback, setCashRegisterFeedback] = useState({ open: false, message: '', severity: 'success' });
    const [loading, setLoading] = useState(false);
    const [textLoading, setTextLoading] = useState('Finalizando...');

    // Estados para loading inicial da tela
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [loadingCashRegister, setLoadingCashRegister] = useState(true);
    const [openPrintDialog, setOpenPrintDialog] = useState(false);
    const [printProgress, setPrintProgress] = useState(0);
    const [messageSnackBar, setMessageSnackBar] = useState('');
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [updatingItem, setUpdatingItem] = useState(false);
    const [useSearchByCode, setUseSearchByCode] = useState(false);

    // Estados para loading moderno
    const [loadingState, setLoadingState] = useState({
        isAddingItem: false,
        addingItemName: '',
        progress: 0,
        showProgress: false,
        showSuccess: false
    });

    // Estado para loading de remoção de itens
    const [deletingItems, setDeletingItems] = useState(new Set());

    // Estado para loading de incremento/decremento de itens
    const [updatingItems, setUpdatingItems] = useState(new Set());

    // Estados para modal de edição de produto
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        quantity: 1,
        value: '',
        item_discount_value: ''
    });

    const getPaymentBaseTotal = () =>
        saleState.total_discount !== '0,00' ? saleState.total_discount : saleState.total.toString();

    const syncSinglePaymentValues = (totalValue) => {
        setCreditValue(totalValue);
        setDebitValue(totalValue);
        setPixValue(totalValue);
        setCashValue(totalValue);
    };

    const parsePtBrCurrency = (value) => {
        if (typeof value === 'number') return value;
        if (!value) return 0;
        const normalized = String(value).replace(/\./g, '').replace(',', '.');
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const formatPtBrCurrency = (value) =>
        Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    /** Texto das parcelas da maquininha (dropdown) só aparece quando não é à vista (1x). Considera só o número inicial da opção. */
    const isCreditParcelado = (installmentValue) => {
        const match = String(installmentValue ?? '').trim().match(/^(\d+)/);
        if (!match) return false;
        return parseInt(match[1], 10) > 1;
    };

    const itemDiscountTotal = saleState.cart.reduce(
        (acc, item) => acc + parsePtBrCurrency(item.item_discount_amount),
        0
    );

    // Carrega os clientes para o seletor de cliente da venda.
    useEffect(() => {
        saleService.getCustomers()
            .then((response) => setCustomerOptions(Array.isArray(response.data) ? response.data : []))
            .catch((error) => console.error('Erro ao carregar clientes:', error));
    }, []);

    const customerToOption = (customer) => ({
        id: customer.ID || customer.Id || customer.id,
        name: customer.Name || '',
        sub: customer.Cellphone || ''
    });

    // Identifica (ou remove) o cliente da venda. Sem venda iniciada, o cliente fica em memória
    // e é enviado na criação da venda (ao adicionar o primeiro item); com venda iniciada,
    // é persistido na hora.
    const handleChangeSaleCustomer = async (option) => {
        const previous = saleCustomer;
        setSaleCustomer(option);
        if (!saleState.id) return;
        try {
            await saleService.setSaleCustomer(saleState.id, option?.id);
        } catch (error) {
            setSaleCustomer(previous);
            setUiState(prev => ({
                ...prev,
                showAttention: true,
                attentionMessage: error?.response?.data?.error || 'Não foi possível identificar o cliente na venda.'
            }));
        }
    };

    const handleCustomerCreated = (customer) => {
        const option = customerToOption(customer);
        if (!option.id) return;
        setCustomerOptions(prev => [...prev, customer]);
        handleChangeSaleCustomer(option);
        setShowCustomerSelect(false);
    };

    // Reset states method
    const resetStates = () => {
        setLastSaleId(saleState.id);
        setSaleCustomer(null);
        setShowCustomerSelect(false);
        setSaleState({
            id: '',
            total: '0,00',
            totalItems: 0,
            cart: [],
            total_discount: '0,00',
            total_item_discount: '0,00',
            subtotal: '0,00',
            remaining_value: '0,00',
            reason: '',
            productList: saleState.productList,
            cash_register_name: '',
            cash_register_id: ''
        });

        setUiState({
            openPayment: false,
            showInstallments: false,
            showSearch: false,
            showAttention: false,
            showSuccess: false,
            attentionMessage: '',
            searchProduct: '',
            searchProductByCode: '',
            installments: 1,
            isProductGeneric: false
        });

        setDiscountState({
            applied: false,
            value: '0,00',
            percentage: 0
        });

        setPaymentType('single');
        setCreditValue('');
        setDebitValue('');
        setPixValue('');
        setCashValue('');
        setSelectedMachine('');
        setPayments([]);
        setInstallments([]);
        setOpenDialog(false);
        setOpenDialogSuccess(false);
        setPixType('key');
        setDisabledPayment(false);
        setOpenDialogCancelSale(false);
        setOpenDialogPrint(false);
        setOpenDialogGenericProduct(false);
        setGenericProductData({
            product_name: '',
            price: '',
            quantity: 1,
            is_generic: false,
            item_discount_value: ''
        });
        setItemDiscountDialog({ open: false, item: null, value: '', loading: false });
    };

    const handleAddPayment = async (type, value, installments) => {
        if (!value) return;

        const paymentId = await saleService.getPaymentId(type);


        var typePayment = "";
        switch (type) {
            case 'CRÉDITO':
                typePayment = 'credit';
                break;
            case 'DÉBITO':
                typePayment = 'debit';
                break;
            case 'PIXKEY':
                typePayment = 'pix_key';
                break;
            case 'PIXQRCODE':
                typePayment = 'pix_qrcode';
                break;
            case 'DINHEIRO':
                typePayment = 'cash';
                break;
        }
        var payment = {
            payment_id: paymentId,
            value: value,
            installment: installments,
            type: typePayment,
            amount_sale: saleState.total_discount !== '0,00' ? saleState.total_discount : saleState.total,
            machine_id: selectedMachine
        }

        if (typePayment === 'cash') {
            const baseTotal = saleState.total_discount !== '0,00' ? saleState.total_discount : saleState.total;
            // Pagamento múltiplo: o troco é sobre o valor ainda devido (restante), não o total da venda.
            const amountForChange = paymentType === 'multiple' ? saleState.remaining_value : baseTotal;
            const changeCash = await handleGetChangeCash(amountForChange, cashValue);

            payment.change = changeCash;
            payment.value = cashValue;

        }

        //setPayments([...payments, payment]);
        payments.push(payment);

        if (paymentType === 'single') {
            setDisabledPayment(true);
        } else if (paymentType === 'multiple') {

            const response = await saleService.getRemainingValue(saleState.id, payments);

            if (response.data.remainder !== '0,00') {
                setSaleState(prev => ({ ...prev, remaining_value: response.data.remainder }));
            } else {
                setSaleState(prev => ({ ...prev, remaining_value: '0,00' }));
            }
        }
    };

    const handleRemovePayment = async (index) => {
        const newPayments = payments.filter((_, i) => i !== index);
        setPayments(newPayments);
        if (paymentType === 'single') {
            setDisabledPayment(false);
        } else if (paymentType === 'multiple') {
            const response = await saleService.getRemainingValue(saleState.id, newPayments);
            if (response.data.remainder !== '0,00') {
                setSaleState(prev => ({ ...prev, remaining_value: response.data.remainder }));
            } else {
                setSaleState(prev => ({ ...prev, remaining_value: '0,00' }));
            }
        }
    };
    const handleFinishSale = async () => {
        setOpenDialog(false);
        if (!printReceiptEnabled) {
            // Impressão desativada nas configurações: finaliza direto, sem oferecer o comprovante.
            await handleConfirmFinishSale(false);
            return;
        }
        setOpenDialogPrint(true);
    };

    const handleConfirmFinishSale = async (shouldPrint = false) => {
        setLoading(true);
        setTextLoading(shouldPrint ? 'Imprimindo...' : 'Finalizando...');
        const responseFinish = await saleService.finishSale(saleState.id);
        const response = await saleService.payment(saleState.id, payments);
        setLoading(false);

        setOpenDialogPrint(false);
        setUiState(prev => ({ ...prev, openPayment: false, showSuccess: true }));

        // Aqui você pode adicionar a lógica de impressão se shouldPrint for true
        if (shouldPrint) {
            // Implementar lógica de impressão do comprovante
            handlePrintSale();
            console.log('Imprimindo comprovante da venda...');
        }

        resetStates();
    };

    const handleCancelSale = async () => {
        setLoading(true);
        const response = await saleService.cancelSale(saleState.id, saleState.reason);
        setOpenDialogSuccess(true);
        setOpenDialog(false);
        setUiState(prev => ({ ...prev, openPayment: false }));
        setPayments([]);
        setOpenDialogCancelSale(false);
        resetStates();
        setLoading(false);
    }

    const handleContinueSale = async (saleId) => {

        const response = await saleService.getSaleToContinue(saleId);

        setSaleCustomer(
            response.data.customer_id
                ? { id: response.data.customer_id, name: response.data.customer_name || '' }
                : null
        );

        setSaleState(prev => {
            const items = (response.data.items || []).map((item) =>
                mapApiSaleItemToCartItem(item, prev.productList)
            );

            const grossSubtotal = response.data.items_gross_subtotal ?? response.data.sale_total ?? '0,00';
            const netAfterItemDiscounts = response.data.net_after_item_discounts ?? response.data.sale_total ?? '0,00';
            const saleTotal = response.data.sale_total ?? '0,00';
            const hasItemDiscount =
                grossSubtotal !== netAfterItemDiscounts &&
                grossSubtotal !== saleTotal;
            const totalItemDiscount =
                response.data.total_item_discount ??
                (hasItemDiscount
                    ? formatPtBrCurrency(parsePtBrCurrency(grossSubtotal) - parsePtBrCurrency(netAfterItemDiscounts))
                    : '0,00');

            return {
                ...prev,
                cart: items,
                total: saleTotal,
                totalItems: response.data.total_itens,
                subtotal: grossSubtotal,
                total_item_discount: totalItemDiscount,
                id: response.data.sale_id,
                cash_register_name: response.data.cash_register_name || '',
                cash_register_id: response.data.cash_register_id || ''
            };
        });

    }

    const refreshCartFromApi = async (saleId) => {
        const [itemsRes, totalRes] = await Promise.all([
            saleService.getSaleItems(saleId),
            saleService.getSaleTotal(saleId)
        ]);
        const raw = itemsRes.data;
        const rows = Array.isArray(raw) ? raw : raw?.items ?? [];
        const mappedRows = rows.map((row) => mapApiSaleItemToCartItem(row, saleState.productList));

        const totalItemDiscountValue = mappedRows.reduce(
            (acc, row) => acc + parsePtBrCurrency(row.item_discount_amount),
            0
        );
        const netSubtotalValue = mappedRows.reduce(
            (acc, row) => acc + parsePtBrCurrency(row.subtotal),
            0
        );
        const grossSubtotalValue = netSubtotalValue + totalItemDiscountValue;
        const hasItems = mappedRows.length > 0;

        setSaleState((prev) => ({
            ...prev,
            cart: mappedRows,
            total: totalRes.data,
            subtotal: hasItems ? formatPtBrCurrency(grossSubtotalValue) : totalRes.data,
            total_item_discount: formatPtBrCurrency(totalItemDiscountValue)
        }));
    };

    const canCreateCustomer = hasPermission('customer.create') || hasPermission('sale.create');
    const canApplyItemDiscount =hasPermission('sale.discount.item');

    const openItemDiscountDialog = (item) => {
        setItemDiscountDialog({
            open: true,
            item,
            value: item.item_discount_type || '',
            loading: false
        });
    };

    const handleSubmitItemDiscount = async (removeOnly) => {
        const { item } = itemDiscountDialog;
        if (!item || !saleState.id) return;
        setItemDiscountDialog((prev) => ({ ...prev, loading: true }));
        try {
            const value = removeOnly ? '' : itemDiscountDialog.value.trim();
            if (!removeOnly && !value) {
                setUiState((prev) => ({
                    ...prev,
                    showAttention: true,
                    attentionMessage: 'Informe o desconto (ex.: 10% ou 10,00) ou use Remover.'
                }));
                setItemDiscountDialog((prev) => ({ ...prev, loading: false }));
                return;
            }
            const response = await saleService.applyItemDiscount({
                sale_id: saleState.id,
                product_id: item.product_id,
                is_generic: Boolean(item.is_generic),
                product_name: item.is_generic ? (item.product_name || item.name || '').trim() : '',
                value
            });
            const status = response?.status ?? 0;
            if (status < 200 || status >= 300) {
                const msg =
                    messageFromApiErrorBody(response.data) ||
                    'Erro ao aplicar desconto no item';
                setUiState((prev) => ({ ...prev, showAttention: true, attentionMessage: msg }));
                setItemDiscountDialog((prev) => ({ ...prev, loading: false }));
                return;
            }
            await refreshCartFromApi(saleState.id);
            setItemDiscountDialog({ open: false, item: null, value: '', loading: false });
        } catch (error) {
            const msg =
                messageFromApiErrorBody(error.response?.data) ||
                error.message ||
                'Erro ao aplicar desconto no item';
            setUiState((prev) => ({ ...prev, showAttention: true, attentionMessage: msg }));
            setItemDiscountDialog((prev) => ({ ...prev, loading: false }));
        }
    };


    // Effects
    useEffect(() => {
        if (id) {
            handleContinueSale(id).catch((error) => {
                if (!error?.isForbidden) {
                    console.error('Erro ao continuar venda:', error);
                }
            });
        }
        loadInitialData();
        handleGetMachineCards();
        handleGetCashRegisterList();
        fetchCompanyData();
    }, [id]);

    useEffect(() => {
        handleCartEffects();
    }, [saleState.cart, id]);

    // Não definir automaticamente o primeiro caixa da lista
    // O caixa deve ser definido apenas pelo retorno do getCashRegister
    // para garantir que apenas caixas selecionados/abertos sejam exibidos

    // Auto-select cash register when dropdown is shown and there's only one option
    useEffect(() => {
        if (uiState.showSelectCashRegister &&
            uiState.cash_register_list &&
            uiState.cash_register_list.length === 1) {
            const singleCashRegister = uiState.cash_register_list[0];
            // Only auto-select if not already selected
            if (saleState.cash_register_id !== singleCashRegister.id) {
                handleSelectCashRegister(singleCashRegister.id).then((response) => {
                    setSaleState(prev => ({
                        ...prev,
                        cash_register_name: singleCashRegister.name,
                        cash_register_id: singleCashRegister.id
                    }));
                    setUiState(prev => ({ ...prev, showSelectCashRegister: false }));
                }).catch((error) => {
                    console.error('Erro ao selecionar caixa automaticamente:', error);
                });
            }
        }
    }, [uiState.showSelectCashRegister, uiState.cash_register_list]);

    // Monitorar quando ambos os loadings terminarem para finalizar o loading inicial
    useEffect(() => {
        if (!loadingProducts && !loadingCashRegister) {
            setInitialLoading(false);
        }
    }, [loadingProducts, loadingCashRegister]);

    // Initialization
    const loadInitialData = async () => {
        try {
            // Carregar produtos
            const products = await stockService.getAvailableProducts();
            setSaleState(prev => ({ ...prev, productList: products.data }));
            setLoadingProducts(false);

            // Carregar dados do caixa
            const response = await saleService.getCashRegister();
            if (response.data && response.data.cash_register_id) {
                setSaleState(prev => ({
                    ...prev,
                    cash_register_id: response.data.cash_register_id,
                    cash_register_name: response.data.cash_register_name
                }));
            } else {
                // Se nenhum caixa foi retornado, limpar o estado
                setSaleState(prev => ({
                    ...prev,
                    cash_register_id: '',
                    cash_register_name: ''
                }));
            }
            setLoadingCashRegister(false);
        } catch (error) {
            console.error('Erro ao carregar dados iniciais:', error);
            setLoadingProducts(false);
            setLoadingCashRegister(false);
        } finally {
            // O loading inicial será finalizado no useEffect que monitora os estados
        }
    };

    // Cart Management
    const handleCartEffects = async () => {
        if (tableRef.current && saleState.cart.length > 10) {
            tableRef.current.scrollTop = tableRef.current.scrollHeight;
        }

    };

    const addToCart = async (product, quantity = 1) => {

        // Inicia o loading
        setLoadingState({
            isAddingItem: true,
            addingItemName: product.name,
            progress: 0,
            showProgress: true
        });

        // Simula progresso para melhor UX
        const progressInterval = setInterval(() => {
            setLoadingState(prev => ({
                ...prev,
                progress: Math.min(prev.progress + 15, 90)
            }));
        }, 100);

        try {
            let currentSaleId = saleState.id;

            if (!currentSaleId) {
                const response = await saleService.createSale(saleCustomer?.id);
                if (response.data.error) {
                    setUiState(prev => ({ ...prev, showAttention: true, attentionMessage: response.data.error }));
                    return;
                }
                currentSaleId = response.data.id;
                setSaleState(prev => ({
                    ...prev,
                    id: currentSaleId,
                    number: response.data.number,
                    cash_register_name: response.data.cash_register_name,
                    cash_register_id: response.data.cash_register_id || ''
                }));
            }

            // Verificar se é um produto genérico e se já existe um com o mesmo nome
            if (product.is_generic || product.isGeneric) {
                const existingGenericItem = ProductManagerService.findExistingProduct(saleState.cart, product);
                if (existingGenericItem) {
                    setUiState(prev => ({
                        ...prev,
                        showAttention: true,
                        attentionMessage: `O produto "${product.product_name}" já está na lista. Use a opção de editar quantidade se desejar alterar.`
                    }));
                    return;
                }
            }


            const { shouldUpdate, existingItem } = ProductManagerService.shouldUpdateProduct(saleState.cart, product);

            let response;
            if (shouldUpdate && existingItem) {

                existingItem.quantity = existingItem.quantity + quantity;
                quantity = existingItem.quantity;
                response = await ProductManagerService.updateProductInCart(existingItem, currentSaleId);
            } else {
                response = await ProductManagerService.addProductToCart(product, currentSaleId, quantity);
            }

            await updateCartState(response, product, quantity, currentSaleId);



            const totalResponse = await saleService.getSaleTotal(currentSaleId);
            const totalItemsResponse = await saleService.getSaleTotalItems(currentSaleId);

            setSaleState(prev => ({
                ...prev,
                total: totalResponse.data,
                subtotal: totalResponse.data,
                totalItems: totalItemsResponse.data.count
            }));

            setUiState(prev => ({ ...prev, showAttention: false }));
            // Id da venda (criada agora ou já existente): usado pela folha mobile "Adicionar à venda".
            return currentSaleId;
        } catch (error) {
            console.error('Error adding product to cart:', error);
            setUiState(prev => ({
                ...prev,
                showAttention: true,
                attentionMessage: error.message || 'Erro ao adicionar produto'
            }));
        } finally {
            clearInterval(progressInterval);
            // Finaliza o progresso
            setLoadingState(prev => ({
                ...prev,
                progress: 100,
                showProgress: false,
                showSuccess: true
            }));

            // Remove o loading após um breve delay
            setTimeout(() => {
                setLoadingState({
                    isAddingItem: false,
                    addingItemName: '',
                    progress: 0,
                    showProgress: false,
                    showSuccess: false
                });
            }, 300);
        }
    };

    const updateCartState = async (response, product, quantity, saleId) => {
        try {
            if (response.data.error) {
                setUiState(prev => ({ ...prev, showAttention: true, attentionMessage: response.data.error }));
                return;
            }

            // Verificar se é um produto existente ou novo usando a nova lógica
            const { shouldUpdate, existingItem } = ProductManagerService.shouldUpdateProduct(saleState.cart, product);

            if (shouldUpdate && existingItem) {

                const updatedCart = saleState.cart.map(item => {
                    if (ProductManagerService.isGenericProduct(product)) {
                        // Para produtos genéricos, comparar por nome
                        if (item.is_generic && item.product_name === product.name) {
                            const newQuantity = item.quantity + quantity;

                            return {
                                ...item,
                                quantity: newQuantity,
                                total: response.data.amountItem, // Calcular total corretamente
                                price: product.price // Atualizar preço se diferente
                            };
                        }
                    } else {

                        if (item.product_id === product.id) {
                            return {
                                ...item,
                                quantity: item.quantity,
                                total: response.data.amountItem
                            };
                        }
                    }
                    return item;
                });
                const totalItemsResponse = await saleService.getSaleTotalItems(saleState.id);
                setSaleState(prev => ({
                    ...prev,
                    cart: updatedCart,
                    totalItems: totalItemsResponse.data.count
                }));
            } else {
                const newItem = {
                    product_id: product.id || product.product_id,
                    product_name: product.name || product.product_name,
                    name: product.name || product.product_name, // Manter compatibilidade
                    price: product.sale_price || product.price,
                    quantity: quantity,
                    total: response.data.amountItem,
                    image: getProductImage(product.id || product.product_id),
                    is_generic: product.is_generic
                };

                const totalItemsResponse = await saleService.getSaleTotalItems(saleState.id);
                setSaleState(prev => ({
                    ...prev,
                    cart: [...prev.cart, newItem],
                    totalItems: quantity,
                }));
            }




        } catch (error) {

            setUiState(prev => ({
                ...prev,
                showAttention: true,
                attentionMessage: error.message || 'Erro ao atualizar carrinho'
            }));
        }
    };

    const updateExistingCartItem = async (existingItem, product, quantity, saleId) => {

        try {
            if (updatingItem) {
                product.quantity = quantity;
                product.update_quantity = true;
            } else {
                product.quantity = existingItem.quantity + quantity;
            }

            const response = await ProductManagerService.addProductToCart(product, saleId, quantity);

            if (response.data.error) {
                setUiState(prev => ({ ...prev, showAttention: true, attentionMessage: response.data.error }));
                return;
            }

            const updatedCart = saleState.cart.map(item =>
                item.product_id === (product.id || product.product_id)
                    ? { ...item, quantity: product.quantity, total: response.data.amountItem }
                    : item
            );

            // Atualizar o total após atualizar o item
            const totalResponse = await saleService.getSaleTotal(saleId);
            const totalItemsResponse = await saleService.getSaleTotalItems(saleState.id);
            setSaleState(prev => ({
                ...prev,
                cart: updatedCart,
                total: totalResponse.data,
                subtotal: totalResponse.data,
                totalItems: totalItemsResponse.data.count
            }));

            return response;
        } catch (error) {
            console.error('Erro ao atualizar item do carrinho:', error);
            setUiState(prev => ({
                ...prev,
                showAttention: true,
                attentionMessage: error.message || 'Erro ao atualizar produto'
            }));
        }
    };

    const handleDecrementItem = async (itemId, itemName) => {
        // Adicionar item ao estado de loading

        setUpdatingItems(prev => new Set([...prev, `${itemId}-${itemName}`]));


        const item = saleState.cart.find(i => i.product_id === itemId && i.name === itemName);
        try {
            // Usar o ProductManagerService para decrementar o item
            const response = await ProductManagerService.decrementProduct(item, saleState.id);

            if (response.data.error) {
                setUiState(prev => ({ ...prev, showAttention: true, attentionMessage: response.data.error }));
                return;
            }


            if (item.quantity === 1) {
                const newCart = saleState.cart.filter(i => {
                    // Para produtos genéricos, usar tanto id quanto name para identificação única
                    if (i.is_generic) {
                        return !(i.product_id === itemId && i.name === itemName);
                    } else {
                        return i.product_id !== itemId;
                    }
                });
                const responseAmount = await saleService.getSaleTotal(saleState.id);
                setSaleState(prev => ({
                    ...prev,
                    cart: newCart,
                    totalItems: prev.totalItems - 1,
                    total: newCart.length === 0 ? 0 : responseAmount.data,
                    subtotal: newCart.length === 0 ? 0 : responseAmount.data
                }));
            } else {
                const updatedCart = saleState.cart.map(i => {
                    // Para produtos genéricos, usar tanto id quanto name para identificação única
                    if (i.is_generic) {
                        return (i.product_id === itemId && i.product_name === itemName)
                            ? { ...i, quantity: i.quantity - 1, total: response.data.amount_item }
                            : i;
                    } else {
                        // Para produtos normais, usar apenas o id
                        return i.product_id === itemId
                            ? { ...i, quantity: i.quantity - 1, total: response.data.amount_item }
                            : i;
                    }
                });
                const responseAmount = await saleService.getSaleTotal(saleState.id);
                setSaleState(prev => ({
                    ...prev,
                    cart: updatedCart,
                    totalItems: prev.totalItems - 1,
                    total: responseAmount.data,
                    subtotal: responseAmount.data
                }));

            }



        } catch (error) {
            console.error('Error decrementing item:', error);
        } finally {
            // Remover item do estado de loading
            setUpdatingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(`${itemId}-${itemName}`);
                return newSet;
            });
        }
    };

    const handleIncrementItem = async (itemId, itemName) => {
        // Adicionar item ao estado de loading

        setUpdatingItems(prev => new Set([...prev, `${itemId}-${itemName}`]));

        try {
            const item = saleState.cart.find(i => i.product_id === itemId && i.name === itemName);


            // Verificar estoque apenas para produtos padrão
            if (ProductManagerService.isStandardProduct(item)) {
                const stockCheck = await stockService.checkStock(itemId);
                if (!stockCheck.data.available) {
                    setUiState(prev => ({
                        ...prev,
                        showAttention: true,
                        attentionMessage: 'Quantidade insuficiente em estoque'
                    }));
                    return;
                }
            }

            // Usar o ProductManagerService para incrementar o item
            const response = await ProductManagerService.incrementProduct(item, saleState.id);

            if (response.data.error) {
                setUiState(prev => ({ ...prev, showAttention: true, attentionMessage: response.data.error }));
                return;
            }

            if (response.data.error) {
                setUiState(prev => ({ ...prev, showAttention: true, attentionMessage: response.data.error }));
                return;
            }

            const updatedCart = saleState.cart.map(item => {
                // Para produtos genéricos, usar tanto id quanto name para identificação única
                if (item.is_generic) {
                    return (item.product_id === itemId && item.name === itemName)
                        ? { ...item, quantity: item.quantity + 1, total: response.data.amount_item }
                        : item;
                } else {
                    // Para produtos normais, usar apenas o id
                    return item.product_id === itemId
                        ? { ...item, quantity: item.quantity + 1, total: response.data.amount_item }
                        : item;
                }
            });


            const responseAmount = await saleService.getSaleTotal(saleState.id);
            setSaleState(prev => ({
                ...prev,
                cart: updatedCart,
                totalItems: prev.totalItems + 1,
                total: responseAmount.data,
                subtotal: responseAmount.data
            }));

        } finally {
            // Remover item do estado de loading
            setUpdatingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(`${itemId}-${itemName}`);
                return newSet;
            });
        }
    };

    // UI Handlers
    const handlePaymentClick = () => {
        setUiState(prev => ({ ...prev, openPayment: !prev.openPayment, showSearch: false, }));
        if (paymentType === 'single') {
            syncSinglePaymentValues(getPaymentBaseTotal());
        }
    };

    const handleSearchToggle = () => {
        setUiState(prev => ({ ...prev, showSearch: !prev.showSearch }));
    };

    const handleSearchProduct = async (code) => {
        if (!code) return;

        try {
            // Limpar mensagens de erro anteriores
            setUiState(prev => ({ ...prev, showAttention: false }));

            const productId = await stockService.getProductByCode(code);
            const product = await stockService.getProduct(productId);

            // Verificação mais robusta do produto
            if (!product || !product.data || !product.data.id) {
                setUiState(prev => ({
                    ...prev,
                    showAttention: true,
                    attentionMessage: 'Produto não encontrado',
                    searchProduct: ''
                }));
                return;
            }

            const stockCheck = await stockService.checkStock(productId);
            if (!stockCheck.data.available && !product.data.isGeneric) {
                setUiState(prev => ({
                    ...prev,
                    showAttention: true,
                    attentionMessage: 'Produto não possui estoque',
                    searchProduct: ''
                }));
                return;
            }

            // Limpar campo de busca antes de processar
            setUiState(prev => ({ ...prev, searchProduct: '' }));

            if (product.data.is_generic) {
                setGenericProductData({
                    product_id: product.data.id,
                    product_name: product.data.name,
                    price: product.data.price || product.data.sale_price || '',
                    quantity: 1,
                    is_generic: true,
                    item_discount_value: ''
                });
                setOpenDialogGenericProduct(true);
                return;
            }

            await addToCart(product.data);

        } catch (error) {
            console.error('Error processing product:', error);
            setUiState(prev => ({
                ...prev,
                showAttention: true,
                attentionMessage: 'Erro ao processar produto',
                searchProduct: ''
            }));
        }
    };

    // Desconto no item recém adicionado (folha mobile "Adicionar à venda"): mesma chamada de `handleSubmitItemDiscount`.
    const applyDiscountToNewItem = async (saleId, productId, value) => {
        try {
            const response = await saleService.applyItemDiscount({
                sale_id: saleId,
                product_id: productId,
                is_generic: false,
                product_name: '',
                value
            });
            const status = response?.status ?? 0;
            if (status < 200 || status >= 300) {
                const msg = messageFromApiErrorBody(response.data) || 'Erro ao aplicar desconto no item';
                setUiState((prev) => ({ ...prev, showAttention: true, attentionMessage: msg }));
                return;
            }
            await refreshCartFromApi(saleId);
        } catch (error) {
            const msg = messageFromApiErrorBody(error.response?.data) || error.message || 'Erro ao aplicar desconto no item';
            setUiState((prev) => ({ ...prev, showAttention: true, attentionMessage: msg }));
        }
    };

    // Seleção de um produto da lista de busca (usado pelo desktop e pela variante mobile).
    // `options` (só mobile): quantidade inicial e desconto opcional no item.
    const handleSelectProduct = async (item, options = {}) => {
        const { quantity = 1, discount = '' } = options || {};
        let productId;
        if (!item || !item.id) return;

        if (item.id) {
            productId = item.id;
        } else {
            productId = await stockService.getProductByCode(item);
        }
        const product = await stockService.getProduct(productId);

        if (!product.data) {
            setUiState(prev => ({
                ...prev,
                showAttention: true,
                attentionMessage: 'Produto não encontrado'
            }));
            return;
        }

        // Verificar se é um produto genérico (testar ambas as propriedades)
        if (product.data.is_generic) {
            setGenericProductData({
                product_id: product.data.id,
                product_name: product.data.name,
                price: product.data.price || '' || product.data.sale_price,
                quantity: 1,
                is_generic: true,
                item_discount_value: ''
            });
            setOpenDialogGenericProduct(true);
        } else {
            const saleId = await addToCart(product.data, quantity);
            if (saleId && discount) {
                await applyDiscountToNewItem(saleId, product.data.id, discount);
            }
            return Boolean(saleId);
        }
    };

    const getProductImage = (id) => {
        const product = saleState.productList.find(p => p.id === id);
        return product?.image;
    };

    const handleUpdateGenericItem = async (item) => {
        if (!ProductManagerService.isGenericProduct(item)) return;

        try {
            const response = await ProductManagerService.updateProductInCart(item, saleState.id);
            const status = response?.status ?? 0;
            if (status < 200 || status >= 300 || response.data?.error) {
                const msg =
                    messageFromApiErrorBody(response.data) ||
                    (typeof response.data === 'string' && response.data.trim()) ||
                    'Erro ao atualizar produto genérico';
                setUiState((prev) => ({
                    ...prev,
                    showAttention: true,
                    attentionMessage: msg
                }));
                return;
            }
            await refreshCartFromApi(saleState.id);
        } catch (error) {
            console.error('Error updating generic item:', error);
            setUiState(prev => ({
                ...prev,
                showAttention: true,
                attentionMessage: error.message || 'Erro ao atualizar produto genérico'
            }));
        }
    };

    const handleEditItem = (item) => {
        setEditingItem(item);
        setUpdatingItem(true);

        setEditFormData({
            name: item.name,
            quantity: item.quantity,
            value: item.price,
            item_discount_value: item.item_discount_type || ''
        });
        setOpenEditDialog(true);
    };

    const handleSaveEdit = async () => {
        setLoading(true);
        if (!editingItem) return;

        try {
            // Atualizar o item no carrinho

            setUpdatingItem(true);


            // Se for produto genérico, atualizar no backend
            if (editingItem.is_generic) {

                const normalizeName = (name) => (name || '').trim().toLowerCase();
                const newName = normalizeName(editFormData.name);
                const nameCollides =
                    newName !== normalizeName(editingItem.product_name) &&
                    saleState.cart.some(
                        (i) =>
                            i !== editingItem &&
                            i.is_generic &&
                            i.product_id === editingItem.product_id &&
                            normalizeName(i.product_name) === newName
                    );
                if (nameCollides) {
                    setUiState(prev => ({
                        ...prev,
                        showAttention: true,
                        attentionMessage: `O produto "${editFormData.name.trim()}" já está na lista. Use um nome diferente.`
                    }));
                    return;
                }

                const updatedItem = {
                    ...editingItem,
                    product_name: editFormData.name,
                    original_product_name: editingItem.product_name,
                    quantity: editFormData.quantity,
                    value: editFormData.value,
                    ...(editFormData.item_discount_value
                        ? { item_discount_value: editFormData.item_discount_value }
                        : {})
                };
                await handleUpdateGenericItem(updatedItem);
            } else {
                const updatedItem = {
                    ...editingItem,
                    product_name: editFormData.name,
                    quantity: editFormData.quantity,
                    value: editFormData.value
                };
                await updateExistingCartItem(updatedItem, updatedItem, editFormData.quantity, saleState.id);
                const totalResponse = await saleService.getSaleTotal(saleState.id);
                setSaleState(prev => ({
                    ...prev,
                    total: totalResponse.data,
                    subtotal: totalResponse.data
                }));
            }

            setOpenEditDialog(false);
            setEditingItem(null);
            setUpdatingItem(false);
            const totalResponse = await saleService.getSaleTotal(saleState.id);
            setSaleState(prev => ({
                ...prev,
                total: totalResponse.data,
                subtotal: totalResponse.data
            }));
        } catch (error) {
            console.error('Error saving edit:', error);
            setUiState(prev => ({
                ...prev,
                showAttention: true,
                attentionMessage: 'Erro ao salvar as alterações do produto'
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setOpenEditDialog(false);
        setEditingItem(null);
        setEditFormData({
            name: '',
            quantity: 1,
            value: '',
            item_discount_value: ''
        });
    };

    const handleDeleteItem = async (itemId, itemName) => {

        const item = saleState.cart.find(i => i.product_id === itemId && i.name === itemName);
        if (!item) return;

        // Adicionar item ao estado de loading
        setDeletingItems(prev => new Set([...prev, `${itemId}-${itemName}`]));

        try {
            // Usar o ProductManagerService para remover produto do carrinho
            const response = await ProductManagerService.removeProductFromCart(item, saleState.id);

            if (response.data.error) {
                setUiState(prev => ({ ...prev, showAttention: true, attentionMessage: response.data.error }));
                return;
            }

            const updatedCart = saleState.cart.filter(i => {
                if (item.is_generic) {
                    // Para produtos genéricos, usar combinação de ID e nome para identificação única
                    return !(i.product_id === itemId && i.name === item.name);
                } else {
                    // Para produtos normais, usar apenas o ID
                    return i.product_id !== itemId;
                }
            });
            const responseAmount = await saleService.getSaleTotal(saleState.id);
            const totalItemsResponse = await saleService.getSaleTotalItems(saleState.id);

            setSaleState(prev => ({
                ...prev,
                cart: updatedCart,
                total: responseAmount.data,
                subtotal: responseAmount.data,
                totalItems: totalItemsResponse.data.count
            }));
        } finally {
            // Remover item do estado de loading
            setDeletingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(`${itemId}-${itemName}`);
                return newSet;
            });
        }
    };

    // `valueOverride` (string) é usado pela variante mobile, que informa o desconto direto no diálogo;
    // no desktop o handler é chamado pelo onClick (evento) e usa o valor do estado, como sempre.
    const handleApplyDiscount = async (valueOverride) => {
        const discountValueToApply = typeof valueOverride === 'string' ? valueOverride : discountState.value;
        if (discountValueToApply === '0,00') {
            setDiscountState(prev => ({ ...prev, error: true }))
            return;
        }
        try {
            const response = await saleService.applyDiscount(saleState.id, discountValueToApply);
            const status = response?.status ?? 0;
            if (status < 200 || status >= 300) {
                const msg =
                    messageFromApiErrorBody(response.data) ||
                    'Erro ao aplicar desconto';
                setUiState((prev) => ({ ...prev, showAttention: true, attentionMessage: msg }));
                return;
            }
            setDiscountState(prev => ({ ...prev, applied: true, error: false }))
            setSaleState(prev => ({ ...prev, total_discount: response.data.remainder, discount_applied: response.data.discount_total }));
            if (paymentType === 'single') {
                syncSinglePaymentValues(response.data.remainder);
            } else if (paymentType === 'multiple') {
                setSaleState(prev => ({ ...prev, remaining_value: response.data.remainder, discount_applied: response.data.discount_total }));
            }
        } catch (error) {
            const msg =
                messageFromApiErrorBody(error.response?.data) ||
                error.message ||
                'Erro ao aplicar desconto';
            setUiState((prev) => ({ ...prev, showAttention: true, attentionMessage: msg }));
        }
    };

    // Remove o desconto da venda (mesma lógica usada no drawer de pagamento do desktop).
    const handleRemoveSaleDiscount = () => {
        setDiscountState({ applied: false, value: '0,00', percentage: 0 });
        setSaleState(prev => ({
            ...prev,
            total_discount: '0,00',
            discount_applied: '0,00',
            remaining_value: paymentType === 'multiple' ? saleState.total : prev.remaining_value
        }));
        if (paymentType === 'single') {
            syncSinglePaymentValues(saleState.total.toString());
        }
    };

    // Mobile: a tela "Finalizar Pagamento" do app só trabalha com pagamento por restante (múltiplo)
    // e descarta os pagamentos lançados ao voltar.
    const handleOpenMobilePayment = () => {
        setPaymentType('multiple');
        setCreditValue('');
        setDebitValue('');
        setPixValue('');
        setCashValue('');
        setSelectedMachine('');
        setPayments([]);
        setInstallments([]);
        setDisabledPayment(false);
        setSaleState(prev => ({ ...prev, remaining_value: getPaymentBaseTotal() }));
        setUiState(prev => ({ ...prev, openPayment: true, showSearch: false, installments: 1 }));
    };

    const handleCloseMobilePayment = () => {
        setPayments([]);
        setDisabledPayment(false);
        setSelectedMachine('');
        setSaleState(prev => ({ ...prev, remaining_value: '0,00' }));
        setUiState(prev => ({ ...prev, openPayment: false }));
    };

    const printSaleById = async (saleId) => {
        const response = await saleService.printSale(saleId);
        if (!response || response.status !== 200) {
            throw new Error('Erro ao imprimir venda');
        }
    };

    const handleGetInstallments = async (saleId, saleTotal, maxInstallments) => {
        const response = await saleService.getInstallments(saleId, saleTotal, maxInstallments);
        setInstallments(response.data);
    };

    const handleGetMachineCards = async () => {
        try {
            const response = await saleService.getMachineCards();
            setMachineCards(response.data);
        } catch (error) {
            if (!error?.isForbidden) {
                console.error('Erro ao carregar máquinas de cartão:', error);
            }
        }
    };

    const handleGetChangeCash = async (amount, received) => {
        const response = await saleService.getChangeCash(amount, received);
        return response.data.cash;
    };

    const handleSelectCashRegister = async (cashRegisterId) => {
        const response = await saleService.selectCashRegister(saleState.id, cashRegisterId);
        return response;
    }

    const handleChangeMachine = (machine) => {
        const machineCard = machineCards.find(card => card.ID === machine);
        const machineId = machineCard.ID;
        setSelectedMachine(machineId);
        if (paymentType === 'single') {
            handleGetInstallments(saleState.id, saleState.total, machineCard.MaxAmountInstallment);
        } else {
            handleGetInstallments(saleState.id, creditValue, machineCard.MaxAmountInstallment);
        }

    };

    const handleGetCashRegisterList = async () => {
        try {
            const response = await saleService.getCashRegisterList();
            setUiState(prev => ({ ...prev, cash_register_list: response.data }));
        } catch (error) {
            console.error('Erro ao carregar caixas:', error);
            setCashRegisterFeedback({
                open: true,
                message: 'Erro ao carregar lista de caixas. Tente novamente.',
                severity: 'error'
            });
        }
    };

    const handlePrintSale = async () => {
        try {
            const response = await saleService.printSale(saleState.id);

            if (!response || response.status !== 200) {
                setSaleState(prev => ({ ...prev, hasError: true, errorMessage: 'Erro ao tentar imprimir comprovante. Tente novamente mais tarde' }));
            }
        } catch (error) {
            console.error('Erro ao imprimir comprovante:', error);
            setSaleState(prev => ({ ...prev, hasError: true, errorMessage: 'Erro ao tentar imprimir comprovante. Tente novamente mais tarde' }));
        }
    }

    const handleConfirmGenericProduct = async () => {
        // Proteção contra múltiplas execuções simultâneas
        if (loading || loadingState.isAddingItem) {
            return;
        }

        setLoading(true);

        try {
            // Usar o ProductManagerService para validar o produto genérico

            const validation = ProductManagerService.validateProduct(genericProductData);
            if (!validation.isValid) {
                setUiState(prev => ({
                    ...prev,
                    showAttention: true,
                    attentionMessage: validation.errors.join(', ')
                }));
                return;
            }


            // Criar produto genérico usando o serviço
            const genericProduct = ProductManagerService.genericService.createGenericProduct({
                ...genericProductData,
            });


            await addToCart(genericProduct, genericProductData.quantity);
            setOpenDialogGenericProduct(false);
        } catch (error) {
            console.error('Erro ao confirmar produto genérico:', error);
            setUiState(prev => ({
                ...prev,
                showAttention: true,
                attentionMessage: error.message || 'Erro ao adicionar produto genérico'
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleCancelGenericProduct = () => {
        setOpenDialogGenericProduct(false);
        setGenericProductData({
            product_name: '',
            price: '',
            quantity: 1,
            item_discount_value: ''
        });
    };

    const fetchCompanyData = async () => {

        try {
            const response = await httpClient.get('/enterprise/account-info');

            if (response.status === 200 && response.data) {
                setCompanyData(response.data);
            }
        } catch (error) {
            if (!error?.isForbidden) {
                console.error('Erro ao carregar dados da empresa:', error);
            }
        }
    };

    const handlePrint = async () => {
        setOpenPrintDialog(true);
        setPrintProgress(0);

        // Incrementa o progresso gradualmente
        const interval = setInterval(() => {
            setPrintProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    return 100;
                }
                return prev + 15;
            });
        }, 50);

        setTimeout(async () => {
            clearInterval(interval);
            setPrintProgress(100);
            setOpenPrintDialog(false);
            try {
                await handlePrintSale();
            } catch (error) {
                console.error('Erro ao imprimir comprovante:', error);
                setMessageSnackBar('Erro ao imprimir comprovante');
                setOpenSnackBar(true);
            }
        }, 1000);
    }

    /**
     * Abre um método de pagamento no acordeão do drawer, fechando os demais
     * (DESIGN-SYSTEM.md §5.7 — "only one open at a time, chevron rotates").
     */
    const openPayMethod = (key) => {
        setUiState((prev) => ({
            ...prev,
            showCreditCard: key === 'credit' ? !prev.showCreditCard : false,
            showDebitCard: key === 'debit' ? !prev.showDebitCard : false,
            showPix: key === 'pix' ? !prev.showPix : false,
            showCash: key === 'cash' ? !prev.showCash : false
        }));
    };

    // Mostrar skeleton enquanto carrega os dados iniciais
    if (initialLoading) {
        return isMobile ? <MobileSaleSkeleton /> : <SaleSkeleton />;
    }

    return (
        <PosShell>
            {isMobile ? (
                <MobileSaleView
                    saleState={saleState}
                    setSaleState={setSaleState}
                    setSaleHasError={(hasError) => setSaleState(prev => ({ ...prev, hasError }))}
                    uiState={uiState}
                    setUiState={setUiState}
                    discountState={discountState}
                    payments={payments}
                    loading={loading}
                    setLoading={setLoading}
                    loadingState={loadingState}
                    updatingItems={updatingItems}
                    deletingItems={deletingItems}
                    hasPermission={hasPermission}
                    canApplyItemDiscount={canApplyItemDiscount}
                    canCreateCustomer={canCreateCustomer}
                    identifyCustomerEnabled={identifyCustomerEnabled}
                    printReceiptEnabled={printReceiptEnabled}
                    saleCustomer={saleCustomer}
                    customerOptions={customerOptions}
                    customerToOption={customerToOption}
                    handleChangeSaleCustomer={handleChangeSaleCustomer}
                    setOpenDialogNewCustomer={setOpenDialogNewCustomer}
                    colorMode={colorMode}
                    toggleColorMode={toggleColorMode}
                    onExit={() => navigate('/home')}
                    itemDiscountDialog={itemDiscountDialog}
                    setItemDiscountDialog={setItemDiscountDialog}
                    openItemDiscountDialog={openItemDiscountDialog}
                    handleSubmitItemDiscount={handleSubmitItemDiscount}
                    openEditDialog={openEditDialog}
                    editingItem={editingItem}
                    editFormData={editFormData}
                    setEditFormData={setEditFormData}
                    handleEditItem={handleEditItem}
                    handleSaveEdit={handleSaveEdit}
                    handleCancelEdit={handleCancelEdit}
                    openDialogCancelSale={openDialogCancelSale}
                    setOpenDialogCancelSale={setOpenDialogCancelSale}
                    handleCancelSale={handleCancelSale}
                    openDialogGenericProduct={openDialogGenericProduct}
                    genericProductData={genericProductData}
                    setGenericProductData={setGenericProductData}
                    handleConfirmGenericProduct={handleConfirmGenericProduct}
                    handleCancelGenericProduct={handleCancelGenericProduct}
                    handleIncrementItem={handleIncrementItem}
                    handleDecrementItem={handleDecrementItem}
                    handleDeleteItem={handleDeleteItem}
                    handleSelectProduct={handleSelectProduct}
                    handleSearchProduct={handleSearchProduct}
                    handleApplyDiscount={handleApplyDiscount}
                    handleRemoveSaleDiscount={handleRemoveSaleDiscount}
                    handlePrint={handlePrint}
                    handleSelectCashRegister={handleSelectCashRegister}
                    selectedMachine={selectedMachine}
                    setSelectedMachine={setSelectedMachine}
                    machineCards={machineCards}
                    installments={installments}
                    creditValue={creditValue}
                    setCreditValue={setCreditValue}
                    debitValue={debitValue}
                    setDebitValue={setDebitValue}
                    pixValue={pixValue}
                    setPixValue={setPixValue}
                    cashValue={cashValue}
                    setCashValue={setCashValue}
                    getPaymentBaseTotal={getPaymentBaseTotal}
                    onOpenPayment={handleOpenMobilePayment}
                    onClosePayment={handleCloseMobilePayment}
                    handleAddPayment={handleAddPayment}
                    handleRemovePayment={handleRemovePayment}
                    handleChangeMachine={handleChangeMachine}
                    handleGetInstallments={handleGetInstallments}
                    handleConfirmFinishSale={handleConfirmFinishSale}
                    printSaleById={printSaleById}
                />
            ) : (
            <>
            <PosAppBar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em' }}>
                        {companyData.trade_name || 'FlowVendas'}
                    </Typography>
                    <Typography sx={{ fontSize: 11, opacity: 0.82, display: { xs: 'none', sm: 'block' } }}>Sistema Frente de Caixa</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Tooltip title={colorMode === 'light' ? 'Alternar para tema escuro' : 'Alternar para tema claro'} arrow>
                        <GhostIconButton onClick={toggleColorMode}>
                            {colorMode === 'light' ? <Moon size={16} /> : <Sun size={16} />}
                        </GhostIconButton>
                    </Tooltip>
                    <Tooltip title="Sair para o painel" arrow>
                        <GhostIconButton onClick={() => navigate('/home')}>
                            <LogOut size={16} />
                        </GhostIconButton>
                    </Tooltip>
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,.16)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0
                        }}
                    >
                        {logoLoading ? (
                            <LinearProgress sx={{ width: 20 }} />
                        ) : companyLogo ? (
                            <img src={companyLogo} alt="Logo da empresa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <CircleUserRound size={20} />
                        )}
                    </Box>
                </Box>
            </PosAppBar>

            <PosBody>
                <PosLeft>
                    <SearchCard>
                        <SearchLabel>
                            <Barcode size={14} strokeWidth={2} />
                            Busca rápida
                        </SearchLabel>

                        <Collapse in={loadingState.showProgress}>
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Adicionando {loadingState.addingItemName}...
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        {loadingState.progress}%
                                    </Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={loadingState.progress} sx={{ height: 6, borderRadius: 3 }} />
                            </Box>
                        </Collapse>

                        <Collapse in={loadingState.showSuccess}>
                            <Box sx={{ mb: 2 }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.25,
                                        py: 0.5,
                                        borderRadius: '20px',
                                        backgroundColor: 'success.bg',
                                        color: 'success.main',
                                        fontSize: 11.5,
                                        fontWeight: 700
                                    }}
                                >
                                    <CheckCircle2 size={13} /> {loadingState.addingItemName} adicionado com sucesso!
                                </Box>
                            </Box>
                        </Collapse>

                        <Box
                            sx={{
                                opacity: loadingState.isAddingItem ? 0.6 : 1,
                                transition: 'opacity 0.3s ease',
                                pointerEvents: loadingState.isAddingItem ? 'none' : 'auto'
                            }}
                        >
                            <DefaultAutocomplete
                                autoFocus
                                variant="pos"
                                inputProps={{
                                    endAdornment: <Barcode size={18} strokeWidth={2} style={{ opacity: 0.6 }} />
                                }}
                                key="search-products"
                                id="search-products"
                                placeholder="Nome do produto ou código de barras…"
                                inputValue={uiState.searchProduct}
                                options={saleState.productList.map((product) => ({
                                    id: product.id,
                                    name: product.name,
                                    value: product.price,
                                    image: product.image,
                                    key: product.id
                                }))}
                                handleChange={(e) => {
                                    setUiState(prev => ({ ...prev, searchProduct: e.target.value }))
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearchProduct(e.target.value);
                                        setUiState(prev => ({ ...prev, searchProductByCode: '', useSearchByCode: true }));
                                    }
                                }}
                                handleGetSelected={handleSelectProduct}
                            />
                        </Box>
                    </SearchCard>

                    <CartCard ref={tableRef}>
                        {saleState.cart.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    minHeight: 320,
                                    gap: 1.5,
                                    color: 'text.disabled',
                                    textAlign: 'center',
                                    p: 3
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 78,
                                        height: 78,
                                        borderRadius: '50%',
                                        border: (t) => `2px dashed ${t.palette.divider}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <ShoppingCart size={30} strokeWidth={1.6} />
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'text.primary' }}>
                                    Nenhum produto adicionado ainda
                                </Typography>
                                <Typography variant="caption" sx={{ maxWidth: 280 }}>
                                    Escaneie ou digite o código dos produtos para começar a venda
                                </Typography>
                            </Box>
                        ) : (
                            saleState.cart.map((item) => {
                                const rowKey = `${item.product_id}-${item.name || item.product_name || ''}`;
                                const isUpdating = updatingItems.has(`${item.product_id}-${item.name}`);
                                const isDeleting = deletingItems.has(`${item.product_id}-${item.name}`);
                                return (
                                    <CartItemRow key={rowKey} data-testid="cart-item">
                                        <ItemThumb>
                                            {item.image ? (
                                                <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <Package size={18} strokeWidth={1.8} />
                                            )}
                                        </ItemThumb>

                                        <Box sx={{ flex: 1, minWidth: 0, flexBasis: { xs: 'calc(100% - 58px)', sm: 0 } }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 13.5,
                                                    fontWeight: 700,
                                                    color: 'text.primary',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {item.product_name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: '1px' }}>
                                                R$ {item.price} · un.
                                            </Typography>
                                            {item.item_discount_type ? (
                                                <Typography sx={{ fontSize: 11.5, color: 'success.main', mt: 0.25 }}>
                                                    Desconto: {item.item_discount_type}
                                                    {item.item_discount_amount ? ` (− R$ ${item.item_discount_amount})` : ''}
                                                </Typography>
                                            ) : null}
                                            {(item.subtotal || item.item_discount_amount) ? (
                                                <Typography
                                                    sx={{
                                                        fontSize: 11,
                                                        color: 'text.secondary',
                                                        mt: 0.5,
                                                        pt: 0.5,
                                                        borderTop: (t) => `1px dashed ${t.palette.divider}`
                                                    }}
                                                >
                                                    {item.subtotal ? (
                                                        <>Subtotal <b style={{ color: theme.palette.text.primary }}>R$ {item.subtotal}</b></>
                                                    ) : null}
                                                    {item.subtotal && item.item_discount_amount ? ' · ' : null}
                                                    {item.item_discount_amount ? (
                                                        <>Desconto <b style={{ color: theme.palette.error.main }}>− R$ {item.item_discount_amount}</b></>
                                                    ) : null}
                                                    {' · Líquido '}<b style={{ color: theme.palette.primary.main }}>R$ {item.total}</b>
                                                </Typography>
                                            ) : null}
                                        </Box>

                                        {(hasPermission('sale.create') || hasPermission('sale.create')) && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, flexWrap: 'nowrap', mt: { xs: 0, sm: 0.5 }, ml: { xs: '58px', sm: 0 } }}>
                                                {hasPermission('sale.create') && (
                                                    <QtyButton tone="minus" size="small" aria-label="Diminuir quantidade" disabled={isUpdating} onClick={() => handleDecrementItem(item.product_id, item.name)}>
                                                        <Minus size={13} strokeWidth={2.5} />
                                                    </QtyButton>
                                                )}
                                                <Typography sx={{ width: 22, textAlign: 'center', fontWeight: 800, fontSize: 13 }}>
                                                    {item.quantity}
                                                </Typography>
                                                {hasPermission('sale.create') && (
                                                    <QtyButton tone="plus" size="small" aria-label="Aumentar quantidade" disabled={isUpdating} onClick={() => handleIncrementItem(item.product_id, item.name)}>
                                                        <Plus size={13} strokeWidth={2.5} />
                                                    </QtyButton>
                                                )}
                                            </Box>
                                        )}

                                        <Typography sx={{ width: { xs: 'auto', sm: 88 }, flex: { xs: 1, sm: '0 0 auto' }, minWidth: { xs: 56, sm: 0 }, textAlign: 'right', fontWeight: 800, fontSize: 13.5, color: 'text.primary', flexShrink: 0, mt: { xs: 0, sm: 0.5 } }}>
                                            R$ {item.total}
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5 }, flexWrap: 'nowrap', flexShrink: 0, mt: { xs: 0, sm: 0.5 } }}>
                                            {hasPermission('sale.create') && (
                                                <Tooltip title="Editar produto" arrow>
                                                    <RowIconButton size="small" onClick={() => handleEditItem(item)} sx={{ color: 'primary.main', backgroundColor: 'info.bg', borderColor: 'primary.main', '&:hover': { color: 'primary.main', backgroundColor: 'info.bg', borderColor: 'primary.main', filter: 'brightness(0.96)' } }}>
                                                        <Pencil size={13} />
                                                    </RowIconButton>
                                                </Tooltip>
                                            )}
                                            {canApplyItemDiscount && (
                                                <Tooltip title="Desconto no item" arrow>
                                                    <RowIconButton size="small" onClick={() => openItemDiscountDialog(item)} sx={{ color: 'secondary.main', backgroundColor: 'secondary.bg', borderColor: 'secondary.main', '&:hover': { color: 'secondary.main', backgroundColor: 'secondary.bg', borderColor: 'secondary.main', filter: 'brightness(0.96)' } }}>
                                                        <Tag size={13} />
                                                    </RowIconButton>
                                                </Tooltip>
                                            )}
                                            {hasPermission('sale.create') && (
                                                <Tooltip title="Remover item" arrow>
                                                    <RowIconButton  size="small" disabled={isDeleting} onClick={() => handleDeleteItem(item.product_id, item.name)} sx={{ color: 'error.main', backgroundColor: 'error.bg', borderColor: 'error.main', '&:hover': { color: 'error.main', backgroundColor: 'error.bg', borderColor: 'error.main', filter: 'brightness(0.96)' } }}>
                                                        <Trash2 size={13} />
                                                    </RowIconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </CartItemRow>
                                );
                            })
                        )}
                    </CartCard>
                </PosLeft>

                <PosRight>
                    <Box sx={{ display: 'flex', gap: 1, mb: { xs: 1.5, md: 2 }, flexWrap: 'wrap' }}>
                        <Tooltip title={saleState.cash_register_name ? `Caixa: ${saleState.cash_register_name}` : 'Selecionar caixa'} arrow>
                            <GhostIconButton
                                onClick={() => setUiState(prev => ({ ...prev, showSelectCashRegister: !prev.showSelectCashRegister }))}
                                sx={saleState.cash_register_name ? { backgroundColor: 'rgba(16,185,129,.25)', borderColor: 'rgba(16,185,129,.45)' } : undefined}
                            >
                                <Wallet size={16} />
                            </GhostIconButton>
                        </Tooltip>

                        {identifyCustomerEnabled && (
                        <Tooltip title={saleCustomer ? `Cliente: ${saleCustomer.name}` : 'Adicionar cliente'} arrow>
                            <GhostIconButton
                                aria-label="Adicionar cliente"
                                onClick={() => setShowCustomerSelect(prev => !prev)}
                                sx={saleCustomer || showCustomerSelect ? { backgroundColor: 'rgba(16,185,129,.25)', borderColor: 'rgba(16,185,129,.45)' } : undefined}
                            >
                                <UserPlus size={16} />
                            </GhostIconButton>
                        </Tooltip>
                        )}

                        {saleState.cart.length > 0 && (
                            <>
                                {printReceiptEnabled && (
                                <Tooltip title="Imprimir comprovante" arrow>
                                    <GhostIconButton onClick={handlePrint}>
                                        <Printer size={16} />
                                    </GhostIconButton>
                                </Tooltip>
                                )}
                                {hasPermission('sale.cancel') && (
                                    <Tooltip title="Cancelar venda" arrow>
                                        <GhostIconButtonDanger onClick={() => setOpenDialogCancelSale(true)}>
                                            <XCircle size={16} />
                                        </GhostIconButtonDanger>
                                    </Tooltip>
                                )}
                                <Tooltip title="Finalizar venda" arrow>
                                    <GhostIconButton onClick={handlePaymentClick}>
                                        <DollarSign size={16} />
                                    </GhostIconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>

                    <Collapse in={uiState.showSelectCashRegister}>
                        <Box sx={{ background: 'rgba(255,255,255,.1)', borderRadius: '12px', p: 1.5, mb: 2 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', opacity: 0.85, mb: 1 }}>
                                Selecionar caixa ({(uiState.cash_register_list || []).length})
                            </Typography>
                            <Select
                                fullWidth
                                size="small"
                                displayEmpty
                                value={saleState.cash_register_id || ''}
                                onChange={(e) => {
                                    const selectedItem = (uiState.cash_register_list || []).find(item => item.id === e.target.value);
                                    handleSelectCashRegister(selectedItem?.id).then(() => {
                                        setSaleState(prev => ({ ...prev, cash_register_name: selectedItem.name, cash_register_id: selectedItem.id }));
                                        setUiState(prev => ({ ...prev, showSelectCashRegister: false }));
                                    });
                                }}
                                sx={{ backgroundColor: '#fff', borderRadius: '10px' }}
                            >
                                <MenuItem value="" disabled>Selecionar caixa…</MenuItem>
                                {(uiState.cash_register_list || []).map((item) => (
                                    <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Collapse>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, flexWrap: 'wrap', gap: 1, mb: { xs: 1.5, md: 2 } }}>
                        <PosChip>
                            <Wallet size={14} /> Caixa: <b>{saleState.cash_register_name || 'não selecionado'}</b>
                        </PosChip>
                        {saleState.number > 0 && (
                            <PosChip>
                                <Receipt size={14} /> Venda <b>#{saleState.number}</b>
                            </PosChip>
                        )}
                        <PosChip>
                            <ShoppingCart size={14} /> Itens: <b>{saleState.totalItems}</b>
                        </PosChip>
                        {identifyCustomerEnabled && saleCustomer && (
                            <PosChip sx={{ minWidth: 0 }}>
                                <CircleUserRound size={14} style={{ flexShrink: 0 }} /> Cliente:{' '}
                                <b style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{saleCustomer.name}</b>
                                <Tooltip title="Trocar cliente" arrow>
                                    <Box
                                        component="button"
                                        type="button"
                                        aria-label="Trocar cliente"
                                        onClick={() => setShowCustomerSelect(true)}
                                        sx={{ display: 'inline-flex', background: 'none', border: 0, p: 0, color: 'inherit', cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
                                    >
                                        <Pencil size={12} />
                                    </Box>
                                </Tooltip>
                                <Tooltip title="Remover cliente" arrow>
                                    <Box
                                        component="button"
                                        type="button"
                                        aria-label="Remover cliente"
                                        onClick={() => handleChangeSaleCustomer(null)}
                                        sx={{ display: 'inline-flex', background: 'none', border: 0, p: 0, color: 'inherit', cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
                                    >
                                        <X size={13} />
                                    </Box>
                                </Tooltip>
                            </PosChip>
                        )}
                    </Box>

                    <Collapse in={identifyCustomerEnabled && showCustomerSelect} unmountOnExit>
                    <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
                        <Autocomplete
                            size="small"
                            openOnFocus
                            options={customerOptions.map(customerToOption)}
                            value={saleCustomer}
                            getOptionLabel={(option) => option?.name || ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(e, value) => {
                                handleChangeSaleCustomer(value);
                                if (value) setShowCustomerSelect(false);
                            }}
                            noOptionsText="Nenhum cliente encontrado"
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontWeight: 600, fontSize: 13 }}>{option.name}</span>
                                        {option.sub && <span style={{ fontSize: 11, opacity: 0.7 }}>{option.sub}</span>}
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    sx={standaloneFieldSx}
                                    autoFocus
                                    label="Cliente (opcional)"
                                    placeholder="Buscar cliente pelo nome"
                                />
                            )}
                        />
                        {canCreateCustomer && (
                        <Box
                            onClick={() => setOpenDialogNewCustomer(true)}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                mt: '6px',
                                fontSize: '11.5px',
                                fontWeight: 700,
                                color: '#fff',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.75 }
                            }}
                        >
                            <Plus size={12} strokeWidth={2.5} />
                            Novo cliente
                        </Box>
                        )}
                    </Box>
                    </Collapse>

                    <Box sx={{ mt: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, py: 0.5, opacity: 0.92 }}>
                            <span>Subtotal</span><span>R$ {saleState.subtotal}</span>
                        </Box>
                        {saleState.total_item_discount !== '0,00' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, py: 0.5, color: '#fca5a5' }}>
                                <span>Desconto item</span><span>- R$ {saleState.total_item_discount}</span>
                            </Box>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: 23,
                                fontWeight: 800,
                                color: POS_GRADIENT.posTotal,
                                pt: 1.5,
                                mt: 1,
                                borderTop: '1px dashed rgba(255,255,255,.28)'
                            }}
                        >
                            <span>Total</span><span>R$ {saleState.total}</span>
                        </Box>

                        <FinalizeButton disabled={saleState.cart.length === 0} onClick={handlePaymentClick} startIcon={<CheckCircle2 size={17} />}>
                            Finalizar venda
                        </FinalizeButton>
                    </Box>

                    <Slide direction="up" in={uiState.showSuccess} mountOnEnter unmountOnExit>
                        <Box sx={{ mt: 2, p: 3, borderRadius: '16px', backgroundColor: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.24)', textAlign: 'center' }}>
                            <CheckCircle2 size={40} style={{ marginBottom: 8 }} />
                            <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 1 }}>Venda finalizada!</Typography>
                            <Divider sx={{ borderColor: 'rgba(255,255,255,.3)', mb: 2 }} />
                            <Typography sx={{ fontWeight: 600, mb: 2 }}>Venda realizada com sucesso</Typography>
                            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                {printReceiptEnabled && (
                                <Button
                                    variant="outlined"
                                    startIcon={<Printer size={16} />}
                                    onClick={() => {
                                        handlePrintSale();
                                        setUiState(prev => ({ ...prev, showSuccess: false }));
                                        resetStates();
                                    }}
                                    sx={{ color: '#fff', borderColor: 'rgba(255,255,255,.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,.1)' } }}
                                >
                                    Imprimir comprovante
                                </Button>
                                )}
                                <Button
                                    variant="text"
                                    startIcon={<X size={16} />}
                                    onClick={() => {
                                        setUiState(prev => ({ ...prev, showSuccess: false }));
                                        resetStates();
                                    }}
                                    sx={{ color: '#fff' }}
                                >
                                    Não imprimir
                                </Button>
                            </Box>
                        </Box>
                    </Slide>
                </PosRight>
            </PosBody>
            </>
            )}

            {/* Drawer de pagamento — acordeão de métodos (DESIGN-SYSTEM.md §5.6/§5.7) */}
            <Drawer
                anchor="right"
                open={uiState.openPayment && !isMobile}
                onClose={handlePaymentClick}
                PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, maxWidth: { xs: '100vw', sm: '92vw' }, display: 'flex', flexDirection: 'column' } }}
            >
                <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: (t) => `1px solid ${t.palette.divider}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.5 }}>Finalizar venda</Typography>
                        <Typography variant="body2" color="text.secondary">Escolha as formas de pagamento</Typography>
                    </Box>
                    <IconButton onClick={handlePaymentClick} size="small" sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: '10px' }}>
                        <X size={16} />
                    </IconButton>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                    <ToggleButtonGroup
                        exclusive
                        fullWidth
                        value={paymentType}
                        onChange={(e, value) => {
                            if (!value) return;
                            setPaymentType(value);
                            if (value === 'single') {
                                syncSinglePaymentValues(getPaymentBaseTotal());
                            } else if (value === 'multiple') {
                                setCreditValue('');
                                setDebitValue('');
                                setPixValue('');
                                setCashValue('');
                                setSaleState(prev => ({ ...prev, remaining_value: getPaymentBaseTotal() }));
                            }
                        }}
                        sx={{ mb: 2, width: '100%' }}
                    >
                        <ToggleButton value="single" sx={{ flex: 1 }}>Pagamento único</ToggleButton>
                        <ToggleButton value="multiple" sx={{ flex: 1 }}>Pagamento múltiplo</ToggleButton>
                    </ToggleButtonGroup>

                    {!discountState.applied && hasPermission('sale.discount.sale') && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                            <TextField
                                label="Desconto no total"
                                helperText={discountState.error ? 'Informe o valor do desconto' : 'Opcional — ex.: 10% ou 10,00'}
                                error={Boolean(discountState.error)}
                                fullWidth
                                sx={standaloneFieldSx}
                                placeholder="R$ 0,00"
                                onChange={(e) => setDiscountState(prev => ({ ...prev, value: e.target.value }))}
                            />
                            <RowIconButton onClick={handleApplyDiscount} sx={{ width: 52, height: 52 }}>
                                <Plus size={16} />
                            </RowIconButton>
                        </Box>
                    )}

                    <Box sx={{ p: 2, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: '12px', backgroundColor: 'background.subtle', mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase' }}>
                                {discountState.applied ? 'Subtotal' : 'Total'}
                            </Typography>
                            <Typography fontWeight={800} color="primary.main">R$ {saleState.total}</Typography>
                        </Box>
                        {itemDiscountTotal > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" fontWeight={700} color="error.main" sx={{ textTransform: 'uppercase' }}>Desconto itens</Typography>
                                <Typography fontWeight={800} color="error.main">- R$ {formatPtBrCurrency(itemDiscountTotal)}</Typography>
                            </Box>
                        )}
                        {discountState.applied && (
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" fontWeight={700} color="error.main" sx={{ textTransform: 'uppercase' }}>Desconto</Typography>
                                        <Tooltip title="Remover desconto">
                                            <IconButton
                                                size="small"
                                                onClick={handleRemoveSaleDiscount}
                                            >
                                                <XCircle size={14} color={theme.palette.error.main} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Typography fontWeight={800} color="error.main">- R$ {saleState.discount_applied}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase' }}>Total</Typography>
                                    <Typography fontWeight={800} color="success.main">R$ {saleState.total_discount}</Typography>
                                </Box>
                            </>
                        )}
                        {saleState.remaining_value !== '0,00' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, pt: 1, borderTop: (t) => `1px dashed ${t.palette.divider}` }}>
                                <Typography fontWeight={700}>Falta receber</Typography>
                                <Typography fontWeight={800} color="error.main">R$ {saleState.remaining_value}</Typography>
                            </Box>
                        )}
                    </Box>

                    {!disabledPayment && (
                        <Box
                            data-testid="toggle-payment-methods"
                            onClick={() => setUiState(prev => ({ ...prev, showPaymentOptions: !prev.showPaymentOptions }))}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1.5 }}
                        >
                            <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>Formas de pagamento</Typography>
                            <ChevronDown size={16} style={{ transform: uiState.showPaymentOptions ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                        </Box>
                    )}

                    {uiState.showPaymentOptions && (
                        <>
                            <PayMethodCard>
                                <PayMethodHead id="credit-card-payment-option" onClick={() => openPayMethod('credit')}>
                                    <PayMethodIcon><CreditCard size={17} /></PayMethodIcon>
                                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 13 }}>Cartão de crédito</Typography>
                                    <ChevronDown size={14} style={{ transform: uiState.showCreditCard ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                                </PayMethodHead>
                                <Collapse in={uiState.showCreditCard}>
                                    <Box sx={{ px: 1.75, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                                            <TextField sx={{ ...standaloneFieldSx, width: '50%' }} label="Valor" placeholder="R$ 0,00" value={creditValue} onChange={(e) => setCreditValue(formatCurrency(e.target.value))} />
                                            <TextField select sx={{ ...standaloneFloatingSelectSx, width: '50%' }} label="Maquininha" value={selectedMachine} onChange={(e) => handleChangeMachine(e.target.value)}>
                                                {machineCards.map((n) => (
                                                    <MenuItem key={n.ID} value={n.ID}>{n.Name}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>
                                        <TextField
                                            name="installments"
                                            select
                                            fullWidth
                                            sx={standaloneFloatingSelectSx}
                                            label="Parcelas"
                                            value={uiState.installments}
                                            onChange={(e) => setUiState(prev => ({ ...prev, installments: e.target.value }))}
                                        >
                                            {installments.map((n) => (
                                                <MenuItem data-testid={`installment-option-${n}`} key={n} value={n}>{`${n}`}</MenuItem>
                                            ))}
                                        </TextField>
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                handleAddPayment('CRÉDITO', creditValue, uiState.installments);
                                                setUiState(prev => ({ ...prev, showCreditCard: false, showPaymentOptions: false }));
                                            }}
                                        >
                                            Adicionar pagamento
                                        </Button>
                                    </Box>
                                </Collapse>
                            </PayMethodCard>

                            <PayMethodCard>
                                <PayMethodHead id="debit-card-payment-option" onClick={() => openPayMethod('debit')}>
                                    <PayMethodIcon><CreditCard size={17} /></PayMethodIcon>
                                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 13 }}>Cartão de débito</Typography>
                                    <ChevronDown size={14} style={{ transform: uiState.showDebitCard ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                                </PayMethodHead>
                                <Collapse in={uiState.showDebitCard}>
                                    <Box sx={{ px: 1.75, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                                            <TextField sx={{ ...standaloneFieldSx, width: '50%' }} label="Valor" placeholder="R$ 0,00" value={debitValue} onChange={(e) => setDebitValue(formatCurrency(e.target.value))} />
                                            <TextField select sx={{ ...standaloneFloatingSelectSx, width: '50%' }} label="Maquininha" value={selectedMachine} onChange={(e) => setSelectedMachine(e.target.value)}>
                                                {machineCards.map((n) => (
                                                    <MenuItem key={n.ID} value={n.ID}>{n.Name}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={() => {
                                                handleAddPayment('DÉBITO', debitValue, '1');
                                                setUiState(prev => ({ ...prev, showDebitCard: false, showPaymentOptions: false }));
                                            }}
                                        >
                                            Adicionar pagamento
                                        </Button>
                                    </Box>
                                </Collapse>
                            </PayMethodCard>

                            <PayMethodCard>
                                <PayMethodHead id="pix-payment-option" onClick={() => openPayMethod('pix')}>
                                    <PayMethodIcon><QrCode size={17} /></PayMethodIcon>
                                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 13 }}>Pix</Typography>
                                    <ChevronDown size={14} style={{ transform: uiState.showPix ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                                </PayMethodHead>
                                <Collapse in={uiState.showPix}>
                                    <Box sx={{ px: 1.75, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                                            <TextField name="pix" sx={{ ...standaloneFieldSx, width: '50%' }} label="Valor" placeholder="R$ 0,00" value={pixValue} onChange={(e) => setPixValue(formatCurrency(e.target.value))} />
                                            <TextField name="pixType" select sx={{ ...standaloneFloatingSelectSx, width: '50%' }} label="Tipo" value={pixType} onChange={(e) => setPixType(e.target.value)}>
                                                <MenuItem value="PIXKEY">Pix Chave</MenuItem>
                                                <MenuItem value="PIXQRCODE">Pix QR Code</MenuItem>
                                            </TextField>
                                        </Box>
                                        {pixType === 'PIXQRCODE' && (
                                            <TextField select fullWidth sx={standaloneFloatingSelectSx} label="Maquininha" value={selectedMachine} onChange={(e) => setSelectedMachine(e.target.value)}>
                                                {machineCards.map((n) => (
                                                    <MenuItem key={n.ID} value={n.ID}>{n.Name}</MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={() => {
                                                handleAddPayment(pixType, pixValue, '1');
                                                setUiState(prev => ({ ...prev, showPix: false, showPaymentOptions: false }));
                                            }}
                                        >
                                            Adicionar pagamento
                                        </Button>
                                    </Box>
                                </Collapse>
                            </PayMethodCard>

                            <PayMethodCard>
                                <PayMethodHead id="cash-payment-option" onClick={() => openPayMethod('cash')}>
                                    <PayMethodIcon><Banknote size={17} /></PayMethodIcon>
                                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 13 }}>Dinheiro</Typography>
                                    <ChevronDown size={14} style={{ transform: uiState.showCash ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                                </PayMethodHead>
                                <Collapse in={uiState.showCash}>
                                    <Box sx={{ px: 1.75, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <TextField fullWidth sx={standaloneFieldSx} label="Valor recebido" placeholder="R$ 0,00" value={cashValue} onChange={(e) => setCashValue(formatCurrency(e.target.value))} />
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={() => {
                                                handleAddPayment('DINHEIRO', cashValue, '1');
                                                setUiState(prev => ({ ...prev, showCash: false, showPaymentOptions: false }));
                                            }}
                                        >
                                            Adicionar pagamento
                                        </Button>
                                    </Box>
                                </Collapse>
                            </PayMethodCard>
                        </>
                    )}

                    <Typography sx={{ fontWeight: 700, fontSize: 13.5, mt: 2, mb: 1 }}>Pagamentos adicionados</Typography>
                    <Box sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: '12px', maxHeight: 220, overflowY: 'auto', p: payments.length ? 1 : 2 }}>
                        {payments.length === 0 && (
                            <Typography variant="caption" color="text.disabled">Nenhum pagamento adicionado ainda.</Typography>
                        )}
                        {payments.map((payment, index) => (
                            <PaidRow key={index}>
                                <PayMethodIcon sx={{ width: 28, height: 28, borderRadius: '8px' }}>
                                    {(payment.type === 'credit' || payment.type === 'debit') && <CreditCard size={14} />}
                                    {(payment.type === 'pix_qrcode' || payment.type === 'pix_key') && <QrCode size={14} />}
                                    {payment.type === 'cash' && <Banknote size={14} />}
                                </PayMethodIcon>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>
                                        {payment.type === 'credit' && 'Cartão de crédito'}
                                        {payment.type === 'debit' && 'Cartão de débito'}
                                        {payment.type === 'pix_qrcode' && 'Pix QR Code'}
                                        {payment.type === 'pix_key' && 'Pix Chave'}
                                        {payment.type === 'cash' && 'Dinheiro'}
                                    </Typography>
                                    {payment.type === 'credit' && isCreditParcelado(payment.installment) && (
                                        <Typography variant="caption" color="text.secondary">{payment.installment}</Typography>
                                    )}
                                    {payment.type === 'cash' && payment.change && (
                                        <Typography variant="caption" sx={{ fontWeight: 700 }} color="text.secondary">Troco: R$ {payment.change}</Typography>
                                    )}
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>R$ {payment.value}</Typography>
                                <IconButton size="small" onClick={() => handleRemovePayment(index)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                                    <X size={14} />
                                </IconButton>
                            </PaidRow>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ px: 3, py: 2, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
                    <Button
                        fullWidth
                        size="large"
                        variant="contained"
                        color="success"
                        disabled={payments.length === 0 || saleState.remaining_value !== '0,00'}
                        onClick={() => setOpenDialog(true)}
                        startIcon={<CheckCircle2 size={18} />}
                        sx={{ height: 48, fontSize: 14 }}
                    >
                        Finalizar venda
                    </Button>
                </Box>
            </Drawer>

            <QuickCustomerDrawer
                open={openDialogNewCustomer}
                onClose={() => setOpenDialogNewCustomer(false)}
                onSuccess={handleCustomerCreated}
            />

            <DialogForm
                iconType="error"
                title="Erro"
                content={uiState.attentionMessage}
                onSubmit={() => setUiState(prev => ({ ...prev, showAttention: false }))}
                open={uiState.showAttention && !isMobile}
                onClose={() => setUiState(prev => ({ ...prev, showAttention: false }))}
                hideConfirmButton={true}
                cancelButtonText="Fechar"
            />

            <DialogForm
                iconType="warning"
                title="Atenção"
                content="Deseja realmente finalizar esta venda?"
                onSubmit={handleFinishSale}
                open={openDialog && !isMobile}
                onClose={() => setOpenDialog(false)}
                confirmButtonText="Sim, Finalizar"
                cancelButtonText="Não, Continuar Venda"
            />
            <DialogForm
                iconType="warning"
                title="Deseja realmente cancelar esta venda?"
                content="Informe o motivo do cancelamento"
                onSubmit={handleCancelSale}
                open={openDialogCancelSale && !isMobile}
                onClose={() => setOpenDialogCancelSale(false)}
                loading={loading}
                loadingText={'Processando cancelamento...'}
            >
                <TextField
                    sx={standaloneFieldSx}
                    id="cancel-reason"
                    label="Motivo"
                    fullWidth
                    multiline
                    minRows={3}
                    required
                    value={saleState.reason}
                    onChange={(e) => setSaleState(prev => ({ ...prev, reason: e.target.value }))}
                />
            </DialogForm>
            <DialogForm
                iconType="info"
                title="Imprimir Comprovante"
                content="Deseja imprimir o comprovante da venda?"
                onSubmit={() => handleConfirmFinishSale(true)}
                open={openDialogPrint && !isMobile}
                onClose={() => handleConfirmFinishSale(false)}
                confirmButtonText="Imprimir"
                cancelButtonText="Não Imprimir"
                loading={loading}
                loadingText={textLoading}
            />
            <Snackbar
                open={false}
                autoHideDuration={3000}
                onClose={() => setOpenDialogSuccess(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setOpenDialogSuccess(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
                    Venda finalizada com sucesso
                </Alert>
            </Snackbar>

            {/* Cash Register Selection Feedback */}
            <Snackbar
                open={cashRegisterFeedback.open}
                autoHideDuration={3000}
                onClose={() => setCashRegisterFeedback({ open: false, message: '', severity: 'success' })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setCashRegisterFeedback({ open: false, message: '', severity: 'success' })}
                    severity={cashRegisterFeedback.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {cashRegisterFeedback.message}
                </Alert>
            </Snackbar>

            {/* Error Feedback */}
            <DialogForm
                iconType="error"
                title="Ocorreu um erro"
                content={saleState.errorMessage}
                onSubmit={() => {
                    setSaleState(prev => ({ ...prev, hasError: false }));
                    handlePrintSale();
                }}
                open={saleState.hasError && !isMobile}
                onClose={() => setSaleState(prev => ({ ...prev, hasError: false }))}
                hideConfirmButton={true}
                cancelButtonText="Fechar"
                loading={false}
                loadingText={''}
            />

            <DialogForm
                iconType="info"
                title="Desconto no produto"
                subtitle={itemDiscountDialog.item?.product_name || itemDiscountDialog.item?.name || ''}
                content="Percentual (ex.: 10%) ou valor fixo (ex.: 10,00). Use Remover para limpar o desconto da linha."
                open={itemDiscountDialog.open && !isMobile}
                onClose={() => {
                    if (!itemDiscountDialog.loading) {
                        setItemDiscountDialog({ open: false, item: null, value: '', loading: false });
                    }
                }}
                onCancel={() => {
                    if (!itemDiscountDialog.loading) {
                        setItemDiscountDialog({ open: false, item: null, value: '', loading: false });
                    }
                }}
                onSubmit={() => handleSubmitItemDiscount(false)}
                confirmButtonText="Aplicar"
                cancelButtonText="Cancelar"
                loading={itemDiscountDialog.loading}
                loadingText="Aplicando..."
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        sx={standaloneFieldSx}
                        label="Desconto"
                        fullWidth
                        placeholder="10% ou 10,00"
                        disabled={itemDiscountDialog.loading}
                        value={itemDiscountDialog.value}
                        onChange={(e) => setItemDiscountDialog((prev) => ({ ...prev, value: e.target.value }))}
                    />
                    <Button
                        type="button"
                        variant="text"
                        color="error"
                        size="small"
                        disabled={itemDiscountDialog.loading}
                        onClick={() => handleSubmitItemDiscount(true)}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        Remover desconto
                    </Button>
                </Box>
            </DialogForm>

            {/* Modal para Produto Genérico */}
            <DialogForm
                iconType="info"
                title="Adicionar Produto Genérico"
                content="Preencha os dados do produto genérico"
                onSubmit={handleConfirmGenericProduct}
                open={openDialogGenericProduct && !isMobile}
                onClose={handleCancelGenericProduct}
                confirmButtonText="Adicionar Produto"
                cancelButtonText="Cancelar"
                loading={loading}
                loadingText={'Processando...'}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                    <TextField
                        sx={standaloneFieldSx}
                        disabled={!hasPermission('sale.generic.rename')}
                        label="Nome do Produto"
                        fullWidth
                        value={genericProductData.product_name}
                        onChange={(e) => setGenericProductData(prev => ({ ...prev, product_name: e.target.value }))}
                        placeholder="Digite o nome do produto"
                        required
                    />

                    <TextField

                        sx={standaloneFieldSx}
                        label="Preço"
                        fullWidth
                        value={genericProductData.price}
                        onChange={(e) => setGenericProductData(prev => ({ ...prev, price: formatCurrency(e.target.value) }))}
                        placeholder="0,00"
                        required
                    />

                    <TextField

                        sx={standaloneFieldSx}
                        label="Quantidade"
                        fullWidth
                        type="number"
                        value={genericProductData.quantity}
                        onChange={(e) => setGenericProductData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                        inputProps={{ min: 1 }}
                    />

                    {canApplyItemDiscount && (
                        <TextField

                            sx={standaloneFieldSx}
                            label="Desconto no item (opcional)"
                            fullWidth
                            value={genericProductData.item_discount_value}
                            onChange={(e) => setGenericProductData((prev) => ({ ...prev, item_discount_value: e.target.value }))}
                            placeholder="10% ou 10,00"
                            helperText="Pode ser definido agora ou depois pelo ícone de etiqueta na linha."
                        />
                    )}
                </Box>
            </DialogForm>

            {/* Modal para Edição de Produto */}
            <DialogForm
                iconType="info"
                title="Editar Produto"
                content="Altere os dados do produto conforme necessário"
                open={openEditDialog && !isMobile}
                onClose={handleCancelEdit}
                onSubmit={handleSaveEdit}
                onCancel={handleCancelEdit}
                confirmButtonText="Alterar"
                cancelButtonText="Cancelar"
                loading={loading}
                loadingText={'Processando...'}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        sx={standaloneFieldSx}
                        label="Nome do Produto"
                        value={editFormData.name}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                        disabled={editingItem && !editingItem.is_generic}
                        fullWidth
                    />
                    <TextField
                        sx={standaloneFieldSx}
                        label="Quantidade"
                        type="number"
                        value={editFormData.quantity}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                        fullWidth
                        inputProps={{ min: 1 }}
                    />
                    <TextField
                        sx={standaloneFieldSx}
                        label="Valor Unitário"
                        value={editFormData.value}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, value: e.target.value }))}
                        disabled={editingItem && !editingItem.is_generic}
                        fullWidth
                        placeholder="0,00"
                    />
                    {editingItem?.is_generic && canApplyItemDiscount && (
                        <TextField
                            sx={standaloneFieldSx}
                            label="Desconto no item (opcional)"
                            value={editFormData.item_discount_value}
                            onChange={(e) => setEditFormData((prev) => ({ ...prev, item_discount_value: e.target.value }))}
                            fullWidth
                            placeholder="10% ou 10,00"
                        />
                    )}
                </Box>
            </DialogForm>

            <Dialog open={openPrintDialog} onClose={() => setOpenPrintDialog(false)} aria-labelledby="print-dialog-title" maxWidth="sm" fullWidth>
                <DialogTitle id="print-dialog-title">Imprimindo Comprovante</DialogTitle>
                <DialogContent>
                    <Box sx={{ width: '100%', mt: 2, mb: 1 }}>
                        <LinearProgress variant="determinate" value={printProgress} sx={{ height: 10, borderRadius: 5 }} />
                    </Box>
                </DialogContent>
            </Dialog>
        </PosShell>
    );
}

export default function Sale() {
    return <SaleContent />;
}
